// apps/web/src/reactApp.jsx
// Migrated from src/react/reactApp.jsx
//
// App-Catalog: Route→appKey für Guards/Feature-Flags delegiert an resolveAppKeyFromRoute (src/app-catalog).

import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  lazy,
} from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { tt } from "@tennis-matches/services/i18n";
import { navigateTo } from "@tennis-matches/core/router";
// moduleRegistry wird jetzt aus DB geladen, nicht mehr statisch
import { normalizeRoute as normalizeMatchmixerRoute } from "@tennis-matches/matchmixer-core/routing/flow.js";
import {
  bootstrapWebAuthSession,
  clearStalePasswordRecoveryIntent,
  getAuthDebugSnapshot,
  getSanitizedLocationHrefForDebug,
  hasPendingPasswordRecovery,
  markPendingPasswordRecovery,
  shouldForcePasswordResetFlow,
  syncPasswordRecoveryIntentFromBrowserUrl,
  onAuthStateChange,
  recoverWebAuthSessionAfterBootstrapTimeout,
  setAuthDebugFailure,
  setAuthDebugStep,
  signOut as signOutRemote,
  isInviteAcceptIntentNextPath,
  isSafeRelativeInternalNextPath,
} from "@tennis-matches/services/auth";
import {
  applySubscriptionToLauncherAppKeys,
  canManageTenantSubscription,
  ensureTenantSubscription,
  isSubscriptionCoreAppKey,
  loadTrainerByLoginEmail,
  resolveCoachDeskSelfProfileMenuHref,
  resolveSubscriptionAccess,
  TENANT_SUBSCRIPTION_CHANGED_EVENT,
} from "@tennis-matches/services/trainerstunden";
import {
  areFeatureFlagsReadySync,
  isFeatureEnabledSync,
  loadFeatureFlags,
} from "@tennis-matches/services/feature-flags";
import { loadAppManifests } from "@tennis-matches/services/app-manifests";
import { loadUserSettings } from "@tennis-matches/services/settings";
import { esc } from "@tennis-matches/core/utils/html";
import {
  checkIsGlobalSuperadminRemote,
  collectActiveTenantRolesFromBindings,
  hasBallmatesKiAccessFromEntitlements,
  isFamilyOnlyRoleBindings,
  restrictEffectiveVisibleAppsForFamilyOnly,
  isTeamCaptainOnlyRoleBindings,
  loadMyGovernanceAccessBundleRemote,
  readGovernanceBundleCache,
  invalidateGovernanceBundleCache,
  loadGovernancePendingWaitRemote,
} from "@tennis-matches/services/governance";
import { useMyClubsWithLabels } from "@tennis-matches/services/hub";
import { useAppState, useSetState } from "./useStore.js";
import { useRoute } from "./useRoute.js";
import {
  clearStaleChunkReloadMarker,
  isStaleChunkError,
  reloadAfterStaleChunk,
} from "./utils/staleChunkReload.js";
import {
  AppToastHost,
  LoadingOverlayProvider,
  SuspenseLoaderToken,
} from "@tennis-matches/ui-components";
import { NavigationLoaderProvider } from "./contexts/NavigationLoaderContext.jsx";
import { AppShell } from "./components/platform/AppShell.jsx";
import { GlobalAiAssistantRoot } from "./components/ai/GlobalAiAssistantRoot.jsx";
import { LoginPage } from "./pages/LoginPage.jsx";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.jsx";
import { AuthCallbackPage } from "./pages/AuthCallbackPage.jsx";
import {
  hasActiveTenantRoleBindings,
  isGlobalSuperadminForSession,
  isGlobalSuperadminOnlyRoute,
  normalizeGovernanceAppKey,
} from "@tennis-matches/services/governance";
import {
  ALL_APP_KEYS_PHASE1,
  canAccessAccessRequestsAdmin,
  canAccessRouteApp,
  deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess,
  getEffectiveVisibleAppsForScope,
} from "./utils/governance/canAccessApp.js";
import {
  resolveAppKeyFromRoute,
  getFeatureFlagKeyForRoute,
  resolveLoaderAppKeyFromRoute,
  getAppCatalogEntry,
} from "./app-catalog/index.js";
import {
  canUsePreferredHomeApp,
  normalizePreferredHomeApp,
  PREFERRED_HOME_CHANGED_EVENT,
  readStoredPreferredHomeAppSync,
  resolveHomeRoute,
} from "./utils/home-routing.js";
import { trackAppOpen } from "./utils/home-usage.js";
import { getShellAppIdentityFromRoute } from "./utils/shellAppIdentity.js";
import { resolveShellDocumentTitle } from "./utils/resolveShellDocumentTitle.js";
import { resolveShellAuthIdentity } from "./utils/shellAuthIdentity.js";
import { loadMyProfile } from "@tennis-matches/services/profile";

// Governance-Bundle: harte Obergrenze, damit roleAppAccess.checked nie dauerhaft false bleibt (kein Endlos-Warten).
const GOVERNANCE_BUNDLE_DEADLINE_MS = 20000;
const SHELL_PROFILE_TIMEOUT_MS = 12000;
const TRAINER_ACCESS_TIMEOUT_MS = 12000;

function __promiseWithTimeout(promise, ms, fallbackValue) {
  const deadline = Math.max(1000, Number(ms) || SHELL_PROFILE_TIMEOUT_MS);
  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(fallbackValue), deadline);
    }),
  ]);
}

function __emptyGovernanceAccessState(session, rpcSuperadmin = false) {
  return __buildRoleAppAccessState({
    bindings: [],
    entitlements: [],
    rows: [],
    missing: true,
    authUser: session?.user,
    rpcSuperadmin: Boolean(rpcSuperadmin),
  });
}

function __isHubPlatformRoute(route) {
  const r = String(route || "").trim();
  return (
    r === "hub" ||
    r === "hub-app-detail" ||
    r === "hub-ki-assistant" ||
    r.startsWith("hub-")
  );
}

/** Verwaltung/Clubhub — keine Marketplace-Apps; dürfen nicht zur Feature-Auswahl umgeleitet werden. */
function __isPlatformVerwaltungRoute(route) {
  const r = String(route || "").trim();
  return (
    r === "admin-people" ||
    r === "admin-people-detail" ||
    r === "admin-locations" ||
    r === "clubhub" ||
    r === "access-requests-admin"
  );
}

function __mayRemainOnPlatformVerwaltungRoute(route, bindings, session) {
  if (!__isPlatformVerwaltungRoute(route)) return false;
  if (String(route || "").trim() === "access-requests-admin") {
    return canAccessAccessRequestsAdmin(bindings, session?.user);
  }
  return hasActiveTenantRoleBindings(bindings);
}

/** JWT-Hinweis (app_metadata / user_metadata), ohne Role-Bindings. */
function __jwtHintsGlobalSuperadmin(session) {
  return isGlobalSuperadminForSession([], session?.user);
}

function __applyGovernanceBundleToAccessState(session, bundle, rpcSuperadmin) {
  const bindings =
    bundle?.ok && Array.isArray(bundle.bindings) ? bundle.bindings : [];
  const missing = Boolean(bundle?.missing);
  const entitlements =
    bundle?.ok && Array.isArray(bundle.entitlements) ? bundle.entitlements : [];
  const rows =
    bundle?.ok && Array.isArray(bundle.roleAppAccessRows)
      ? bundle.roleAppAccessRows
      : [];
  const identityRelink = bundle?.identityRelink ?? null;
  return __buildRoleAppAccessState({
    bindings,
    entitlements,
    rows,
    missing,
    identityRelink,
    authUser: session?.user,
    rpcSuperadmin: Boolean(rpcSuperadmin),
  });
}

/** Zwischenstand bei langsamem Bundle: Cache nutzen oder checked=false (kein Redirect mit leeren Rechten). */
function __governanceInterimAccessState(session, rpcSuperadmin) {
  const cached = readGovernanceBundleCache(__asStr(session?.user?.id));
  if (
    cached?.ok &&
    Array.isArray(cached.bindings) &&
    cached.bindings.length > 0
  ) {
    return __buildRoleAppAccessState({
      bindings: cached.bindings,
      entitlements: Array.isArray(cached.entitlements)
        ? cached.entitlements
        : [],
      rows: Array.isArray(cached.roleAppAccessRows)
        ? cached.roleAppAccessRows
        : [],
      missing: true,
      identityRelink: cached.identityRelink ?? null,
      authUser: session?.user,
      rpcSuperadmin: Boolean(rpcSuperadmin),
    });
  }
  return null;
}

/** Eingeloggt: erst starten, wenn Role-Bindings wirklich geladen (nicht checked mit leerem Zwischenstand). */
function __governanceBindingsReady(roleAppAccess, session) {
  if (!session) return true;
  if (!roleAppAccess?.checked) return false;
  if (roleAppAccess.isGlobalSuperAdmin) return true;
  return hasActiveTenantRoleBindings(roleAppAccess.bindings);
}

function __buildRoleAppAccessState({
  bindings,
  entitlements,
  rows,
  missing,
  identityRelink,
  authUser,
  rpcSuperadmin,
}) {
  const list = Array.isArray(bindings) ? bindings : [];
  const ent = Array.isArray(entitlements) ? entitlements : [];
  const raRows = Array.isArray(rows) ? rows : [];
  const derived = deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
    list,
    ent,
    raRows,
    authUser,
  );
  const isGlobalSuperAdmin =
    Boolean(rpcSuperadmin) || isGlobalSuperadminForSession(list, authUser);
  const allowedAppKeys = Array.from(derived?.allowedAppKeys || []);
  return {
    checked: true,
    allApps: false,
    isGlobalSuperAdmin,
    allowedAppKeys,
    bindings: list,
    missing: Boolean(missing),
    entitlements: ent,
    roleAppAccessRows: raRows,
    identityRelink: identityRelink ?? null,
  };
}

// Deferred: breadcrumb-registry + app-manifests + Supabase erst nach First Paint (Performance)
// Kein statischer Import von breadcrumb-registry.js oder @tennis-matches/ui-components/breadcrumb

function __reloadAfterStaleChunkOnce() {
  reloadAfterStaleChunk();
}

function __namedLazy(importModule, exportName) {
  return lazy(() =>
    importModule()
      .then((module) => {
        const Component = module?.[exportName];
        if (!Component) {
          __reloadAfterStaleChunkOnce();
          throw new Error(
            `Lazy module missing ${exportName} export (stale chunk after deploy?)`,
          );
        }
        clearStaleChunkReloadMarker();
        return { default: Component };
      })
      .catch((error) => {
        __reloadAfterStaleChunkOnce();
        throw error;
      }),
  );
}

const LazyDynamicAppLoader = __namedLazy(
  () => import("./modules/DynamicAppLoader.jsx"),
  "DynamicAppLoader",
);

// Lazy: nur bei passender Route geladen (Performance Quick Win #1)
const LazyPlayerMatchesPage = lazy(() =>
  import("./pages/player-matches/PlayerMatchesPage.jsx").then((m) => ({
    default: m.PlayerMatchesPage,
  })),
);
const LazyContentPage = lazy(() =>
  import("./pages/content/ContentPage.jsx").then((m) => ({
    default: m.ContentPage,
  })),
);
// Einstellungen-Hub: /hub/einstellungen (Legacy /settings, /hub/settings)
const LazySettingsHubPage = lazy(() =>
  import("./pages/settings/SettingsHubPage.jsx").then((m) => ({
    default: m.SettingsHubPage,
  })),
);
const LazyGlobalAccountPage = lazy(() =>
  import("./pages/account/GlobalAccountPage.jsx").then((m) => ({
    default: m.GlobalAccountPage,
  })),
);
const LazyDevQaPage = lazy(() =>
  import("./pages/dev/DevQaPage.jsx").then((m) => ({
    default: m.DevQaPage,
  })),
);
const LazyAccessPendingPage = lazy(() =>
  import("./pages/governance/AccessPendingPage.jsx").then((m) => ({
    default: m.AccessPendingPage,
  })),
);
const LazyAccessRequestsAdminPage = lazy(() =>
  import("./pages/governance/AccessRequestsAdminPage.jsx").then((m) => ({
    default: m.AccessRequestsAdminPage,
  })),
);
const LazyAcceptInvitePage = lazy(() =>
  import("./pages/governance/AcceptInvitePage.jsx").then((m) => ({
    default: m.AcceptInvitePage,
  })),
);
const LazyPeopleAdminPage = lazy(() =>
  import("./pages/admin/PeopleAdminPage.jsx").then((m) => ({
    default: m.PeopleAdminPage,
  })),
);
const LazyPlayerDetailPage = lazy(() =>
  import("./pages/admin/PlayerDetailPage.jsx").then((m) => ({
    default: m.PlayerDetailPage,
  })),
);
const LazyLocationsAdminPage = lazy(() =>
  import("./pages/admin/LocationsAdminPage.jsx").then((m) => ({
    default: m.LocationsAdminPage,
  })),
);
const LazyClubHubPage = lazy(() =>
  import("./pages/clubhub/ClubHubPage.jsx").then((m) => ({
    default: m.ClubHubPage,
  })),
);
const LazySuperadminApp = lazy(() => import("@tennis-matches/superadmin"));
const LazyGlobalHubPage = __namedLazy(
  () => import("./pages/hub/GlobalHubPage.jsx"),
  "GlobalHubPage",
);
const LazyKiAssistantPage = lazy(() =>
  import("./pages/hub/KiAssistantPage.jsx").then((m) => ({
    default: m.KiAssistantPage,
  })),
);
const LazyHubMarketplaceAppDetailPage = lazy(() =>
  import("./pages/hub/HubMarketplaceAppDetailPage.jsx").then((m) => ({
    default: m.HubMarketplaceAppDetailPage,
  })),
);
const LazySchleifchenHubPage = lazy(() =>
  import("./pages/hub/SchleifchenHubPage.jsx").then((m) => ({
    default: m.SchleifchenHubPage,
  })),
);
const LazyMatchMixerTemplatesPage = lazy(() =>
  import("./pages/hub/MatchMixerTemplatesPage.jsx").then((m) => ({
    default: m.MatchMixerTemplatesPage,
  })),
);
const LazyMatchMixerOwnTournamentsPage = lazy(() =>
  import("./pages/hub/MatchMixerOwnTournamentsPage.jsx").then((m) => ({
    default: m.MatchMixerOwnTournamentsPage,
  })),
);
const LazyMatchMixerStatsPage = lazy(() =>
  import("./pages/hub/MatchMixerStatsPage.jsx").then((m) => ({
    default: m.MatchMixerStatsPage,
  })),
);
const LazyClubmeisterschaftStatsPage = lazy(() =>
  import("./pages/hub/ClubmeisterschaftStatsPage.jsx").then((m) => ({
    default: m.ClubmeisterschaftStatsPage,
  })),
);
const LazyMatchMixerOpenRegistrationsPage = lazy(() =>
  import("./pages/hub/MatchMixerOpenRegistrationsPage.jsx").then((m) => ({
    default: m.MatchmixerOpenRegistrationsPage,
  })),
);
const LazyMatchMixerRegisterPage = lazy(() =>
  import("./pages/matchmixer/MatchMixerRegisterPage.jsx").then((m) => ({
    default: m.MatchMixerRegisterPage,
  })),
);
const LazyMatchMixerPlayerCarePage = lazy(() =>
  import("./pages/matchmixer/MatchMixerPlayerCarePage.jsx").then((m) => ({
    default: m.MatchMixerPlayerCarePage,
  })),
);
const LazyWinterDoppelJoinHostPage = lazy(() =>
  import("./pages/winterdoppel/WinterDoppelJoinHostPage.jsx").then((m) => ({
    default: m.WinterDoppelJoinHostPage,
  })),
);
const LazyWinterDoppelConfirmEmailHostPage = lazy(() =>
  import("./pages/winterdoppel/WinterDoppelConfirmEmailHostPage.jsx").then(
    (m) => ({
      default: m.WinterDoppelConfirmEmailHostPage,
    }),
  ),
);
const LazyWinterDoppelScheduleHostPage = lazy(() =>
  import("./pages/winterdoppel/WinterDoppelScheduleHostPage.jsx").then((m) => ({
    default: m.WinterDoppelScheduleHostPage,
  })),
);
const LazyClubmeisterschaftHubPage = lazy(() =>
  import("./pages/hub/ClubmeisterschaftHubPage.jsx").then((m) => ({
    default: m.ClubmeisterschaftHubPage,
  })),
);
const LazyTennisweltPage = lazy(() =>
  import("./pages/tenniswelt/TennisweltPage.jsx").then((m) => ({
    default: m.TennisweltPage,
  })),
);
const LazyPublicParentConfirmPage = lazy(() =>
  import("./pages/public/PublicParentConfirmPage.jsx").then((m) => ({
    default: m.PublicParentConfirmPage,
  })),
);
const LazyPublicSessionRsvpPage = lazy(() =>
  import("./pages/public/PublicSessionRsvpPage.jsx").then((m) => ({
    default: m.PublicSessionRsvpPage,
  })),
);
const LazyPublicPayPage = lazy(() =>
  import("./pages/public/PublicPayPage.jsx").then((m) => ({
    default: m.PublicPayPage,
  })),
);
const LazyPublicPaySuccessPage = lazy(() =>
  import("./pages/public/PublicPaySuccessPage.jsx").then((m) => ({
    default: m.PublicPaySuccessPage,
  })),
);
const LazyPublicPayCancelPage = lazy(() =>
  import("./pages/public/PublicPayCancelPage.jsx").then((m) => ({
    default: m.PublicPayCancelPage,
  })),
);
const LazyPublicBookingPage = lazy(() =>
  import("./pages/public/PublicBookingPage.jsx").then((m) => ({
    default: m.PublicBookingPage,
  })),
);
const LazyPublicRegeltermineSwapPage = lazy(() =>
  import("./pages/public/PublicRegeltermineSwapPage.jsx").then((m) => ({
    default: m.PublicRegeltermineSwapPage,
  })),
);
import { ClubmeisterschaftAppWrapper } from "./pages/hub/ClubmeisterschaftAppWrapper.jsx";
import {
  devMark,
  devMeasure,
  devPerfEnabled,
  devTimeAsync,
} from "./utils/devPerf.js";

// Legacy renderers (Phase 1: mounted via per-route React wrapper pages)

// Legacy Breadcrumb component removed - now using generic Breadcrumb from @tennis-matches/ui-components/breadcrumb

// Idempotency guard: suppress duplicate normalizeRoute triggers (StrictMode/HMR/multiple consumers)
let __normLastKey = "";
let __normLastAt = 0;
let __normLastResult = null;
const __NORM_MS = 500;
let __deferredLoadWarned = false;
let __ignoredAbortWarned = false;

/** Opt-in: `localStorage.setItem('tm_debug_routing','1')` → Guard-Redirects loggen. */
function __routingDebugEnabled() {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("tm_debug_routing") === "1"
    );
  } catch (_) {
    return false;
  }
}

function __asStr(x) {
  return String(x ?? "").trim();
}

/** Primäre Login-E-Mail (OAuth: oft nur unter identities[].identity_data). */
function resolveSessionPrimaryEmail(sess) {
  const u = sess?.user;
  if (!u) return "";
  const direct = __asStr(u.email).toLowerCase();
  if (direct) return direct;
  const ids = Array.isArray(u.identities) ? u.identities : [];
  for (const id of ids) {
    const data =
      id?.identity_data && typeof id.identity_data === "object"
        ? id.identity_data
        : {};
    const em = __asStr(
      data.email || data.email_address || data.primary_email,
    ).toLowerCase();
    if (em) return em;
  }
  return "";
}

function __trainerAllowsAppKey(trainerAllowedAppKeys, appKey) {
  const nk = normalizeGovernanceAppKey(__asStr(appKey));
  if (!nk) return false;
  const list =
    trainerAllowedAppKeys instanceof Set
      ? [...trainerAllowedAppKeys]
      : Array.isArray(trainerAllowedAppKeys)
        ? trainerAllowedAppKeys
        : [];
  return list.some((x) => normalizeGovernanceAppKey(__asStr(x)) === nk);
}

function __isAbortError(err) {
  const name = String(err?.name || "").toLowerCase();
  const msg = String(err?.message || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg.includes("operation was aborted") ||
    msg.includes("the user aborted") ||
    msg.includes("user aborted a request") ||
    msg.includes("abgebrochen")
  );
}

function __isDeferredLoadEnvironmentError(err) {
  const msg = String(err?.message || "").toLowerCase();
  const raw = String(err || "").toLowerCase();
  return (
    msg.includes("importing a module script failed") ||
    raw.includes("importing a module script failed") ||
    msg.includes("access control") ||
    raw.includes("access control") ||
    msg.includes("cors") ||
    raw.includes("cors")
  );
}

function __getQueryParam(key) {
  const k = String(key || "").trim();
  if (!k) return "";
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const v = __asStr(sp.get(k) || "");
    if (v) return v;
  } catch (_) {}
  // legacy hash support: "#/route?code=..." or "#route?code=..."
  try {
    const raw = String(window.location.hash || "");
    const q = raw.includes("?") ? raw.split("?")[1] : "";
    if (!q) return "";
    const sp2 = new URLSearchParams(q.startsWith("?") ? q : `?${q}`);
    return __asStr(sp2.get(k) || "");
  } catch (_) {}
  return "";
}

function __decodeURIComponentSafe(s) {
  const raw = String(s || "").trim();
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function __invitePostAcceptGateActive() {
  try {
    return (
      Date.now() <
      Number(sessionStorage.getItem("tm_skip_access_gate_until") || 0)
    );
  } catch {
    return false;
  }
}

function __postRegistrationAccessPendingActive() {
  try {
    return (
      sessionStorage.getItem("tm_post_registration_access_pending") === "1"
    );
  } catch {
    return false;
  }
}

function __clearPostRegistrationAccessPending() {
  try {
    sessionStorage.removeItem("tm_post_registration_access_pending");
  } catch (_) {}
}

function __launchAccessContext(
  session,
  launcherIsSuperAdmin,
  sessionSuperadminJwt,
  rpcSuperadminResolved,
) {
  const knownSuperadmin =
    Boolean(launcherIsSuperAdmin) ||
    Boolean(sessionSuperadminJwt) ||
    rpcSuperadminResolved === true;
  return { knownSuperadmin, sessionSuperadminJwt, rpcSuperadminResolved };
}

/** Superadmin per RPC noch ungeklärt (kein JWT-Hinweis) — kein Zugriffs-Warte-Screen rendern/erzwingen. */
function __superadminIdentityPending(launch) {
  return launch.rpcSuperadminResolved === null && !launch.sessionSuperadminJwt;
}

/**
 * `/` → Hub: keine Umleitung zu Zugriffs-Onboarding/Warte-Screen, solange Superadmin per RPC
 * noch ungeklärt ist oder der Nutzer Superadmin ist (auch bei veraltetem sessionStorage-Flag).
 */
function __hubBlocksAccessGateRedirect(route /* , launch */) {
  // Hub ist die zentrale Onboarding-Oberfläche („Deine Tenniswelt"): jeder
  // eingeloggte Nutzer muss den Hub erreichen können – auch ohne App-Rechte
  // und ohne aktive Tenant-Bindings. Das Onboarding läuft dort über die
  // Hero-Aktivitätskarten statt über den Access-Pending- oder
  // Feature-Selection-Zwischenscreen. Andernfalls landen Nicht-Superadmins
  // beim Klick auf „Startseite" im AppLauncher sofort wieder auf
  // /access-pending statt im Hub.
  return __isHubPlatformRoute(route);
}

const __appKeyForRoute = (route) => resolveAppKeyFromRoute(route);

// Legacy renderers have been removed - all routes now use React components

export function ReactApp() {
  const state = useAppState();
  const setState = useSetState();
  const { route } = useRoute();
  const navigate = useNavigate();
  const location = useLocation();

  // Kanonische URLs: alte Top-Level-Pfade → /matchmixer/… (Query/Hash bleiben erhalten)
  useEffect(() => {
    const path = String(location?.pathname || "").replace(/\/+$/, "") || "/";
    const q = typeof window !== "undefined" ? window.location.search || "" : "";
    const h = typeof window !== "undefined" ? window.location.hash || "" : "";
    const topMap = {
      "/setup": "/matchmixer/setup",
      "/schedule": "/matchmixer/schedule",
      "/entry": "/matchmixer/entry",
      "/finish": "/matchmixer/finish",
      "/results": "/matchmixer/results",
      "/round-config": "/matchmixer/round-config",
    };
    if (topMap[path]) {
      navigate(topMap[path] + q + h, { replace: true });
      return;
    }
    if (path === "/hub/schleifchen") {
      navigate("/hub/matchmixer" + q + h, { replace: true });
      return;
    }
    if (path === "/schleifchen") {
      navigate("/matchmixer" + q + h, { replace: true });
      return;
    }
    const subMatch = path.match(/^\/schleifchen\/([^/]+)$/);
    if (subMatch) {
      navigate(`/matchmixer/${subMatch[1]}` + q + h, { replace: true });
    }
  }, [location?.pathname, navigate]);

  useEffect(() => {
    if (!devPerfEnabled()) return;
    devMark(
      `tm-react-app-mount-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );
  }, []);

  useEffect(() => {
    if (!devPerfEnabled()) return;
    devMark(
      `tm-router-route:${String(route || "").slice(0, 48)}:${Date.now()}`,
    );
  }, [route]);

  // useLayoutEffect: vor Kind-useEffect, damit legacy navigateTo React Router nutzt (nicht history.replaceState).
  useLayoutEffect(() => {
    window.__reactRouterNavigate = (to, opts) => navigate(to, opts);
    return () => {
      delete window.__reactRouterNavigate;
    };
  }, [navigate]);

  // Track i18n messages version to trigger re-render when messages are loaded
  const [i18nVersion, setI18nVersion] = useState(0);
  // Feature flags: ensure cache is loaded so route guard can enforce "superglobal" enables/disables.
  const [flagsTick, setFlagsTick] = useState(0);
  // Deferred: Breadcrumb-Registry + app-manifests + Supabase erst nach First Paint
  const [breadcrumbApi, setBreadcrumbApi] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (areFeatureFlagsReadySync())
      return () => {
        cancelled = true;
      };
    loadFeatureFlags()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFlagsTick((x) => x + 1);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  void flagsTick;

  // Deferred init: KEIN breadcrumb-registry / app-manifests / Supabase im Initial-Load
  useEffect(() => {
    const run = async () => {
      const chainId = `hub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mChainS = `tm-hub-manifest-registry-chain-start-${chainId}`;
      const mChainE = `tm-hub-manifest-registry-chain-end-${chainId}`;
      if (devPerfEnabled()) devMark(mChainS);
      const regMod = await import("./modules/breadcrumb-registry.js");
      await devTimeAsync("tm-initBreadcrumbRegistry", async () => {
        await regMod.initBreadcrumbRegistry();
      });
      const [b, a] = await devTimeAsync(
        "tm-hub-breadcrumb-ui-and-app-loader-import",
        async () =>
          Promise.all([
            import("@tennis-matches/ui-components/breadcrumb"),
            import("./modules/app-loader.js"),
          ]),
      );
      if (!a?.findAppIdForRoute || !b?.Breadcrumb) {
        __reloadAfterStaleChunkOnce();
        throw new Error(
          !a?.findAppIdForRoute
            ? "app-loader module missing findAppIdForRoute export (stale chunk?)"
            : "breadcrumb module missing Breadcrumb export (stale chunk after deploy?)",
        );
      }
      setBreadcrumbApi({
        Breadcrumb: b.Breadcrumb,
        generateFullBreadcrumb: b.generateFullBreadcrumb,
        getAllApps: b.getAllApps,
        findAppForRoute: b.findAppForRoute,
        findAppIdForRoute: a.findAppIdForRoute,
      });
      if (devPerfEnabled()) {
        devMark(mChainE);
        devMeasure(
          `tm-hub-manifest-and-registry-total-${chainId}`,
          mChainS,
          mChainE,
        );
      }
    };
    run().catch((err) => {
      if (__isAbortError(err)) return;
      if (__isDeferredLoadEnvironmentError(err)) {
        if (!__deferredLoadWarned) {
          __deferredLoadWarned = true;
          console.warn(
            "[reactApp] Deferred breadcrumb/registry load skipped (network/CORS environment):",
            err,
          );
        }
        return;
      }
      console.error(
        "[reactApp] Deferred breadcrumb/registry load failed:",
        err,
      );
      if (
        isStaleChunkError(err) ||
        String(err?.message || "")
          .toLowerCase()
          .includes("breadcrumb") ||
        String(err?.message || "")
          .toLowerCase()
          .includes("app-loader")
      ) {
        __reloadAfterStaleChunkOnce();
      }
    });
  }, []);

  useEffect(() => {
    const handleI18nUpdate = () => {
      setI18nVersion((v) => v + 1);
    };
    window.addEventListener("i18n-messages-updated", handleI18nUpdate);
    return () => {
      window.removeEventListener("i18n-messages-updated", handleI18nUpdate);
    };
  }, []);

  // Dev-hardening: avoid noisy unhandled rejections for aborted fetches/navigation churn.
  useEffect(() => {
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (__isAbortError(reason)) {
        event.preventDefault?.();
        if (!__ignoredAbortWarned) {
          __ignoredAbortWarned = true;
          console.warn(
            "[reactApp] Ignored AbortError from async startup flow.",
          );
        }
      }
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () =>
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }, []);

  // Recreate tt function when locale or i18nVersion changes
  const t = useMemo(
    () => tt(state?.app?.locale || "de-DE"),
    [state?.app?.locale, i18nVersion],
  );

  // Route guards (migration parity): normalize flow-based routes like Schleifchen.
  // Normalisierung läuft auch bei ?code= – verhindert Rückkehr zu Setup/Spielplan, wenn Turnier bereits gestartet/beendet.
  const normalizedRoute = useMemo(() => {
    const urlCode =
      new URLSearchParams(window.location.search).get("code") || "";
    const wantNew = __getQueryParam("new") || "";
    const isMatchmixerFlowRoute =
      (String(route || "").startsWith("matchmixer") ||
        String(route || "").startsWith("schleifchen")) &&
      route !== "matchmixer-anmelden" &&
      route !== "matchmixer-me";

    try {
      if (isMatchmixerFlowRoute) {
        const key = `${String(route || "")}|${urlCode}|${wantNew}`;
        const now = Date.now();
        if (__normLastKey === key && now - __normLastAt < __NORM_MS) {
          if (import.meta.env?.DEV) {
            console.log("[host] suppressed duplicate normalizeRoute", key);
          }
          return __normLastResult ?? route;
        }
        __normLastKey = key;
        __normLastAt = now;
        const normalized = normalizeMatchmixerRoute(route, state || {});
        __normLastResult = normalized;
        return normalized;
      }
    } catch (err) {
      console.error("[reactApp] normalizeRoute error:", err);
    }
    return route;
  }, [route, state]);

  useEffect(() => {
    if (!normalizedRoute) return;
    if (normalizedRoute === route) return;

    const urlCode =
      new URLSearchParams(window.location.search).get("code") || "";
    const targetRoute = urlCode
      ? `${normalizedRoute.split("?")[0]}?code=${encodeURIComponent(urlCode)}`
      : normalizedRoute;
    navigateTo(targetRoute, true);
  }, [normalizedRoute, route, state]);

  // ------------------------------------------------------------
  // Auth Route Guard (Phase 0 UX): OAuth-URL verarbeiten, dann Session (mit Timeout, kein endloses Laden)
  // ------------------------------------------------------------
  const [authChecked, setAuthChecked] = useState(false);
  const [session, setSession] = useState(null);
  const [authBootstrapError, setAuthBootstrapError] = useState(null);
  const [authBootstrapRetryKey, setAuthBootstrapRetryKey] = useState(0);

  const authDebugUrlEnabled = useMemo(() => {
    try {
      return (
        new URLSearchParams(String(location?.search || "")).get("debugAuth") ===
        "1"
      );
    } catch {
      return false;
    }
  }, [location.search]);

  const onSignOut = useCallback(async () => {
    try {
      await signOutRemote();
    } catch (_) {}
    try {
      invalidateGovernanceBundleCache();
    } catch (_) {}
    try {
      __clearPostRegistrationAccessPending();
    } catch (_) {}
    try {
      setGovernancePending({ checked: false, shouldWait: false });
    } catch (_) {}
    try {
      setSession(null);
    } catch (_) {}
    navigateTo("login", true);
  }, []);

  const onSignIn = useCallback(() => {
    navigateTo("login");
  }, []);

  const handleAuthBootstrapSessionRecheck = useCallback(() => {
    setAuthBootstrapError(null);
    setAuthChecked(false);
    setAuthBootstrapRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const authId = `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const mStart = `tm-auth-session-start-${authId}`;
    const mEnd = `tm-auth-session-end-${authId}`;
    if (devPerfEnabled()) devMark(mStart);
    (async () => {
      try {
        const s = await bootstrapWebAuthSession({ timeoutMs: 15000 });
        if (cancelled) return;
        syncPasswordRecoveryIntentFromBrowserUrl();
        clearStalePasswordRecoveryIntent(s);
        setSession(s || null);
        setAuthBootstrapError(null);
        setAuthChecked(true);
        if (shouldForcePasswordResetFlow(s)) {
          navigateTo("reset-password", true);
        }
        if (devPerfEnabled()) {
          devMark(mEnd);
          devMeasure(`tm-auth-getSession-${authId}`, mStart, mEnd);
        }
      } catch (e) {
        if (cancelled) return;
        const code = e && typeof e === "object" ? e.code : "";
        if (code === "AUTH_BOOTSTRAP_TIMEOUT") {
          if (
            import.meta.env.DEV &&
            typeof console !== "undefined" &&
            console.warn
          ) {
            console.warn("governance: bootstrap timeout", {
              phase: "bootstrapWebAuthSession",
              note: "attempt_recovery",
            });
          }
          try {
            const recovered = await recoverWebAuthSessionAfterBootstrapTimeout({
              timeoutMs: 5000,
            });
            if (recovered) {
              if (
                import.meta.env.DEV &&
                typeof console !== "undefined" &&
                console.debug
              ) {
                console.debug("auth: session received", {
                  recovered: true,
                  hasSession: true,
                });
                console.debug("auth: session user", {
                  userId: recovered?.user?.id,
                  email: recovered?.user?.email,
                });
              }
              setSession(recovered);
              setAuthBootstrapError(null);
              setAuthChecked(true);
              if (devPerfEnabled()) devMark(mEnd);
              return;
            }
          } catch (_) {}
        }
        if (code !== "AUTH_BOOTSTRAP_TIMEOUT" && __isAbortError(e)) {
          try {
            const recovered = await recoverWebAuthSessionAfterBootstrapTimeout({
              timeoutMs: 8000,
            });
            if (recovered) {
              if (
                import.meta.env.DEV &&
                typeof console !== "undefined" &&
                console.debug
              ) {
                console.debug("auth: session received", {
                  recovered: true,
                  hasSession: true,
                  reason: "abort-recovery",
                });
              }
              setSession(recovered);
              setAuthBootstrapError(null);
              setAuthChecked(true);
              if (devPerfEnabled()) devMark(mEnd);
              return;
            }
          } catch (_) {}
        }
        setSession(null);
        setAuthBootstrapError(
          code === "AUTH_BOOTSTRAP_TIMEOUT" ? "timeout" : "failed",
        );
        setAuthChecked(true);
        if (
          import.meta.env.DEV &&
          code !== "AUTH_BOOTSTRAP_TIMEOUT" &&
          typeof console !== "undefined" &&
          console.warn
        ) {
          console.warn("governance: bootstrap failed", {
            phase: "bootstrapWebAuthSession",
            err: String(e?.message || e),
          });
        }
        if (devPerfEnabled()) devMark(mEnd);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authBootstrapRetryKey]);

  // Nach fehlgeschlagenem Bootstrap: Session kann etwas später via GoTrue eintreffen (Safari / parallele Tabs).
  useEffect(() => {
    if (!authBootstrapError) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const recovered = await recoverWebAuthSessionAfterBootstrapTimeout({
          timeoutMs: 8000,
        });
        if (cancelled || !recovered?.user?.id) return;
        setSession(recovered);
        setAuthBootstrapError(null);
        setAuthChecked(true);
      } catch (_) {}
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authBootstrapError]);

  // Keep session reactive (needed for E-Mail/Passwort-Login ohne OAuth-Redirect).
  useEffect(() => {
    const lastAuthNotifyRef = { event: "", userId: "", token: "" };

    const res = onAuthStateChange((event, nextSession) => {
      const userId = String(nextSession?.user?.id || "");
      const token = String(nextSession?.access_token || "");
      const isDuplicateNotify =
        lastAuthNotifyRef.event === event &&
        lastAuthNotifyRef.userId === userId &&
        lastAuthNotifyRef.token === token;
      if (isDuplicateNotify) return;
      lastAuthNotifyRef.event = event;
      lastAuthNotifyRef.userId = userId;
      lastAuthNotifyRef.token = token;

      if (
        import.meta.env.DEV &&
        typeof console !== "undefined" &&
        console.debug
      ) {
        console.debug("auth: onAuthStateChange", {
          event,
          hasSession: !!nextSession,
          userId: userId || undefined,
        });
      }

      if (event === "PASSWORD_RECOVERY") {
        markPendingPasswordRecovery();
        navigateTo("reset-password", true);
        return;
      }

      // Kein Rematerialisieren nur aus localStorage-E-Mail (24h) — das hat
      // Nutzer nach abgebrochenem Reset dauerhaft auf /reset-password geschickt.
      if (
        event === "SIGNED_IN" &&
        nextSession &&
        hasPendingPasswordRecovery()
      ) {
        navigateTo("reset-password", true);
        return;
      }

      if (nextSession?.user?.id) {
        setAuthBootstrapError(null);
      }

      if (event === "SIGNED_OUT" || !userId) {
        try {
          invalidateGovernanceBundleCache();
        } catch (_) {}
        try {
          __clearPostRegistrationAccessPending();
        } catch (_) {}
      }

      setSession((prev) => {
        const next = nextSession || null;
        const prevUid = __asStr(prev?.user?.id);
        const nextUid = __asStr(next?.user?.id);
        if (prevUid && nextUid && prevUid !== nextUid) {
          try {
            invalidateGovernanceBundleCache();
          } catch (_) {}
          try {
            __clearPostRegistrationAccessPending();
          } catch (_) {}
        }
        if (!prev && !next) return prev;
        if (
          prev?.user?.id === next?.user?.id &&
          prev?.access_token === next?.access_token
        ) {
          return prev;
        }
        return next;
      });
      setAuthChecked(true);
    });
    const unsubscribe = res?.data?.subscription?.unsubscribe || null;

    return () => {
      try {
        if (typeof unsubscribe === "function") unsubscribe();
      } catch (_) {}
    };
  }, []);

  // ------------------------------------------------------------
  // Role Bindings App Access (Phase 1): filter hub + route guard
  // ------------------------------------------------------------
  const [roleAppAccess, setRoleAppAccess] = useState(() => ({
    checked: false,
    allApps: false,
    isGlobalSuperAdmin: false,
    allowedAppKeys: [],
    bindings: [],
    missing: false,
    entitlements: [],
    roleAppAccessRows: [],
    identityRelink: null,
  }));
  const [governancePending, setGovernancePending] = useState(() => ({
    checked: false,
    shouldWait: false,
  }));
  const [accessManifestsState, setAccessManifestsState] = useState(() => ({
    loaded: false,
    manifests: [],
  }));
  const [preferredHomeApp, setPreferredHomeApp] = useState(() =>
    readStoredPreferredHomeAppSync(),
  );
  const [homePreferenceLoaded, setHomePreferenceLoaded] = useState(false);
  const [homeBootstrapDone, setHomeBootstrapDone] = useState(false);
  const [govBundleRevision, setGovBundleRevision] = useState(0);
  /**
   * Globaler Superadmin per RPC (wenn nicht im JWT). `null` = noch unbekannt.
   * Verhindert Hub → Feature-Auswahl-Flash, bis die RPC-Antwort da ist.
   */
  const [rpcSuperadminResolved, setRpcSuperadminResolved] = useState(
    /** @type {null | boolean} */ (null),
  );
  /** Hub/Plattform: nach erstem has_app_access nicht erneut zur Feature-Auswahl werfen (Bundle-Nachladen). */
  const hubPlatformAccessEstablishedRef = useRef(false);
  /** Einmal pro Vollseiten-Load: Startseiten-Präferenz auf hub/home anwenden, nicht bei jedem Hub-Besuch. */
  const initialHomeBootstrapDoneRef = useRef(false);
  /** Verhindert erneutes Governance-Bundle bei gleicher User-ID (Auth-Events liefern neue Session-Objekte). */
  const govBundleLoadedKeyRef = useRef("");
  const govPendingLoadedKeyRef = useRef("");
  const trainerLoadedUserIdRef = useRef("");
  const prevSessionUserIdRef = useRef("");

  useEffect(() => {
    const uid = __asStr(session?.user?.id);
    const prevUid = prevSessionUserIdRef.current;

    if (prevUid && uid && prevUid !== uid) {
      try {
        invalidateGovernanceBundleCache();
      } catch (_) {}
      try {
        __clearPostRegistrationAccessPending();
      } catch (_) {}
      govBundleLoadedKeyRef.current = "";
      govPendingLoadedKeyRef.current = "";
      trainerLoadedUserIdRef.current = "";
      hubPlatformAccessEstablishedRef.current = false;
      initialHomeBootstrapDoneRef.current = false;
      setHomeBootstrapDone(false);
      setGovBundleRevision((n) => n + 1);
      setGovernancePending({ checked: false, shouldWait: false });
      setRoleAppAccess({
        checked: false,
        allApps: false,
        isGlobalSuperAdmin: false,
        allowedAppKeys: [],
        bindings: [],
        missing: false,
        entitlements: [],
        roleAppAccessRows: [],
        identityRelink: null,
        fromCache: false,
      });
    }

    if (!uid) {
      hubPlatformAccessEstablishedRef.current = false;
      initialHomeBootstrapDoneRef.current = false;
      setHomeBootstrapDone(false);
      govBundleLoadedKeyRef.current = "";
      govPendingLoadedKeyRef.current = "";
      trainerLoadedUserIdRef.current = "";
      setRpcSuperadminResolved(null);
      setGovernancePending({ checked: false, shouldWait: false });
      setHomePreferenceLoaded(false);
      setHomeBootstrapDone(false);
    }

    prevSessionUserIdRef.current = uid;
  }, [session?.user?.id]);

  const sessionSuperadminJwt = useMemo(
    () => __jwtHintsGlobalSuperadmin(session),
    [session?.user],
  );

  useEffect(() => {
    if (!authChecked || !session?.user?.id) {
      setRpcSuperadminResolved(null);
      return undefined;
    }
    if (sessionSuperadminJwt) {
      setRpcSuperadminResolved(true);
      return undefined;
    }
    let cancelled = false;
    setRpcSuperadminResolved(null);
    (async () => {
      let isSuperadmin = false;
      try {
        const rpcRes = await checkIsGlobalSuperadminRemote();
        isSuperadmin = Boolean(rpcRes?.ok && rpcRes?.isSuperadmin);
      } catch (_) {
        isSuperadmin = false;
      }
      if (!cancelled) setRpcSuperadminResolved(isSuperadmin);
    })();
    return () => {
      cancelled = true;
    };
  }, [authChecked, session?.user?.id, sessionSuperadminJwt]);

  useEffect(() => {
    let cancelled = false;
    if (!authChecked)
      return () => {
        cancelled = true;
      };
    if (!session?.user?.id) {
      setPreferredHomeApp("hub");
      setHomePreferenceLoaded(false);
      return () => {
        cancelled = true;
      };
    }
    setHomePreferenceLoaded(false);
    (async () => {
      try {
        setAuthDebugStep("profile:start");
        const res = await __promiseWithTimeout(
          loadUserSettings(),
          SHELL_PROFILE_TIMEOUT_MS,
          null,
        );
        if (cancelled) return;
        if (!res) {
          setPreferredHomeApp(readStoredPreferredHomeAppSync());
          setHomePreferenceLoaded(true);
          setAuthDebugStep("profile:timeout");
          return;
        }
        const preferred = normalizePreferredHomeApp(
          res?.settings?.preferredHomeApp || res?.settings?.preferred_home_app,
        );
        setPreferredHomeApp(preferred);
        setHomePreferenceLoaded(true);
        setAuthDebugStep("profile:success");
      } catch (e) {
        if (!cancelled) {
          setAuthDebugFailure(
            "profile:loadUserSettings",
            e,
            "loadUserSettings",
          );
          setPreferredHomeApp(readStoredPreferredHomeAppSync());
          setHomePreferenceLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authChecked, session?.user?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPreferredHomeChanged = (event) => {
      const next = normalizePreferredHomeApp(event?.detail?.preferredHomeApp);
      setPreferredHomeApp(next);
    };
    window.addEventListener(
      PREFERRED_HOME_CHANGED_EVENT,
      onPreferredHomeChanged,
    );
    return () => {
      window.removeEventListener(
        PREFERRED_HOME_CHANGED_EVENT,
        onPreferredHomeChanged,
      );
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await loadAppManifests();
      if (cancelled) return;
      if (res?.ok) {
        setAccessManifestsState({
          loaded: true,
          manifests: Array.isArray(res.manifests) ? res.manifests : [],
        });
      } else {
        setAccessManifestsState({ loaded: true, manifests: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!authChecked)
      return () => {
        cancelled = true;
      };
    if (!session) {
      govBundleLoadedKeyRef.current = "";
      setRoleAppAccess({
        checked: true,
        allApps: false,
        isGlobalSuperAdmin: false,
        allowedAppKeys: [],
        bindings: [],
        missing: false,
        entitlements: [],
        roleAppAccessRows: [],
        identityRelink: null,
      });
      return () => {
        cancelled = true;
      };
    }

    const userId = session.user?.id;
    const loadKey = `${userId}:${govBundleRevision}`;
    if (govBundleLoadedKeyRef.current === loadKey) {
      return () => {
        cancelled = true;
      };
    }

    setRoleAppAccess((p) => {
      if (p.fromCache && hasActiveTenantRoleBindings(p.bindings)) return p;
      if (p.checked && hasActiveTenantRoleBindings(p.bindings)) return p;
      return { ...p, checked: false };
    });
    (async () => {
      const govId = `gov-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mBindS = `tm-governance-bindings-start-${govId}`;
      const mBindE = `tm-governance-bindings-end-${govId}`;
      try {
        if (devPerfEnabled()) devMark(mBindS);
        const tGov =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const govNow = () =>
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const bundlePromise = loadMyGovernanceAccessBundleRemote({
          onBindingsReady: (partial) => {
            if (cancelled) return;
            setRoleAppAccess((prev) => {
              const ent =
                Array.isArray(prev.entitlements) && prev.entitlements.length
                  ? prev.entitlements
                  : Array.isArray(partial.entitlements)
                    ? partial.entitlements
                    : [];
              const rows =
                Array.isArray(prev.roleAppAccessRows) &&
                prev.roleAppAccessRows.length
                  ? prev.roleAppAccessRows
                  : Array.isArray(partial.roleAppAccessRows)
                    ? partial.roleAppAccessRows
                    : [];
              return {
                ...__buildRoleAppAccessState({
                  bindings: Array.isArray(partial.bindings)
                    ? partial.bindings
                    : [],
                  entitlements: ent,
                  rows,
                  missing: Boolean(partial.missing),
                  identityRelink: partial.identityRelink ?? prev.identityRelink,
                  authUser: session?.user,
                  rpcSuperadmin: prev.isGlobalSuperAdmin,
                }),
                fromCache: prev.fromCache,
              };
            });
          },
        });
        const rpcPromise = checkIsGlobalSuperadminRemote();
        let interimApplied = false;
        const timeoutId = setTimeout(async () => {
          if (cancelled || interimApplied) return;
          const elapsed = govNow() - tGov;
          setAuthDebugFailure(
            "governance:bundle-timeout",
            Object.assign(new Error("governance-bundle-deadline"), {
              code: "GOVERNANCE_BUNDLE_TIMEOUT",
            }),
            "loadMyGovernanceAccessBundleRemote",
          );
          if (
            import.meta.env.DEV &&
            typeof console !== "undefined" &&
            console.warn
          ) {
            console.warn("governance: bootstrap timeout (interim)", {
              phase: "loadMyGovernanceAccessBundleRemote",
              deadlineMs: GOVERNANCE_BUNDLE_DEADLINE_MS,
              elapsedMs: Math.round(elapsed * 10) / 10,
            });
          }
          let rpcSuperadmin = false;
          try {
            const rpcRes = await checkIsGlobalSuperadminRemote();
            rpcSuperadmin = Boolean(rpcRes?.ok && rpcRes?.isSuperadmin);
          } catch (_) {}
          const interim = __governanceInterimAccessState(
            session,
            rpcSuperadmin,
          );
          if (interim) {
            setRoleAppAccess(interim);
          } else {
            setRoleAppAccess(
              __emptyGovernanceAccessState(session, rpcSuperadmin),
            );
          }
        }, GOVERNANCE_BUNDLE_DEADLINE_MS);

        let bundle;
        let rpcRes = { ok: false, isSuperadmin: false };
        try {
          [bundle, rpcRes] = await Promise.all([bundlePromise, rpcPromise]);
        } finally {
          clearTimeout(timeoutId);
        }
        if (devPerfEnabled()) {
          devMark(mBindE);
          devMeasure(`tm-governance-role-bindings-${govId}`, mBindS, mBindE);
        }
        if (cancelled) return;
        interimApplied = true;
        const rpcSuperadmin = Boolean(rpcRes?.ok && rpcRes?.isSuperadmin);
        if (rpcSuperadmin) setRpcSuperadminResolved(true);
        setRoleAppAccess(
          __applyGovernanceBundleToAccessState(session, bundle, rpcSuperadmin),
        );
        govBundleLoadedKeyRef.current = loadKey;
        if (devPerfEnabled()) devMark(`tm-governance-complete-${govId}`);
      } catch (err) {
        if (cancelled) return;
        if (!__isAbortError(err)) {
          console.error(
            "[reactApp] Failed to load role app access state:",
            err,
          );
        }
        setAuthDebugFailure(
          "governance:bundle",
          err,
          "loadMyGovernanceAccessBundleRemote",
        );
        if (
          import.meta.env.DEV &&
          typeof console !== "undefined" &&
          console.warn
        ) {
          console.warn("governance: bootstrap failed", {
            phase: "loadMyGovernanceAccessBundleRemote",
            err: String(err?.message || err),
          });
        }
        let rpcRes = { ok: false, isSuperadmin: false };
        try {
          rpcRes = await checkIsGlobalSuperadminRemote();
        } catch (_) {
          rpcRes = { ok: false, isSuperadmin: false };
        }
        const interim = __governanceInterimAccessState(
          session,
          Boolean(rpcRes?.ok && rpcRes?.isSuperadmin),
        );
        if (interim) {
          setRoleAppAccess(interim);
          govBundleLoadedKeyRef.current = loadKey;
        } else {
          setRoleAppAccess(
            __emptyGovernanceAccessState(
              session,
              Boolean(rpcRes?.ok && rpcRes?.isSuperadmin),
            ),
          );
          govBundleLoadedKeyRef.current = loadKey;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authChecked, session?.user?.id, govBundleRevision]);

  useEffect(() => {
    if (!authChecked || !session?.user?.id) return undefined;
    const watchdogId = setTimeout(() => {
      if (!homePreferenceLoaded) {
        setPreferredHomeApp(readStoredPreferredHomeAppSync());
        setHomePreferenceLoaded(true);
      }
      if (!homeBootstrapDone) {
        initialHomeBootstrapDoneRef.current = true;
        setHomeBootstrapDone(true);
      }
      setTrainerAppAccess((prev) =>
        prev.checked
          ? prev
          : {
              checked: true,
              isTrainerLogin: false,
              allowedAppKeys: null,
              trainerRow: null,
            },
      );
      setRoleAppAccess((prev) =>
        prev.checked ? prev : __emptyGovernanceAccessState(session),
      );
      setAccessManifestsState((prev) =>
        prev.loaded ? prev : { loaded: true, manifests: [] },
      );
    }, GOVERNANCE_BUNDLE_DEADLINE_MS + 5000);
    return () => clearTimeout(watchdogId);
  }, [authChecked, session, homePreferenceLoaded, homeBootstrapDone]);

  // Governance-Cache: JWT-Superadmin sofort mit Session.user auswerten (vor RPC/Bundle).
  useEffect(() => {
    if (!authChecked || !session?.user) return;
    setRoleAppAccess((prev) => {
      if (!prev.fromCache) return prev;
      const next = __buildRoleAppAccessState({
        bindings: prev.bindings,
        entitlements: prev.entitlements,
        rows: prev.roleAppAccessRows,
        missing: prev.missing,
        identityRelink: prev.identityRelink,
        authUser: session.user,
        rpcSuperadmin: false,
      });
      if (
        prev.isGlobalSuperAdmin === next.isGlobalSuperAdmin &&
        prev.allApps === next.allApps &&
        prev.allowedAppKeys.length === next.allowedAppKeys.length
      ) {
        return prev;
      }
      return { ...prev, ...next, fromCache: true };
    });
  }, [authChecked, session?.user?.id, session?.user]);

  // Governance-Cache wird mit authUser=null hydratisiert — Superadmin (JWT + RPC) nach Session nachziehen.
  useEffect(() => {
    if (!authChecked || !session?.user) return;
    let cancelled = false;
    (async () => {
      let rpcSuperadmin = false;
      try {
        const rpcRes = await checkIsGlobalSuperadminRemote();
        rpcSuperadmin = Boolean(rpcRes?.ok && rpcRes?.isSuperadmin);
      } catch (_) {
        rpcSuperadmin = false;
      }
      if (cancelled) return;
      setRoleAppAccess((prev) => {
        const next = __buildRoleAppAccessState({
          bindings: prev.bindings,
          entitlements: prev.entitlements,
          rows: prev.roleAppAccessRows,
          missing: prev.missing,
          identityRelink: prev.identityRelink,
          authUser: session.user,
          rpcSuperadmin: rpcSuperadmin,
        });
        if (!prev.checked && !next.isGlobalSuperAdmin) return prev;
        if (
          prev.allApps === next.allApps &&
          prev.isGlobalSuperAdmin === next.isGlobalSuperAdmin &&
          prev.missing === next.missing &&
          prev.allowedAppKeys.length === next.allowedAppKeys.length
        ) {
          return prev;
        }
        return {
          ...prev,
          ...next,
          checked: prev.checked || next.isGlobalSuperAdmin,
          fromCache: prev.fromCache,
        };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [authChecked, session?.user, session?.user?.id]);

  useEffect(() => {
    const onGovRefresh = () => {
      govBundleLoadedKeyRef.current = "";
      govPendingLoadedKeyRef.current = "";
      setGovBundleRevision((n) => n + 1);
    };
    window.addEventListener("tm:governance-bundle-refresh", onGovRefresh);
    return () =>
      window.removeEventListener("tm:governance-bundle-refresh", onGovRefresh);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!authChecked)
      return () => {
        cancelled = true;
      };
    if (!session?.user?.id) {
      govPendingLoadedKeyRef.current = "";
      setGovernancePending({ checked: true, shouldWait: false });
      return () => {
        cancelled = true;
      };
    }

    if (isGlobalSuperadminForSession([], session.user)) {
      govPendingLoadedKeyRef.current = `${session.user.id}:${govBundleRevision}`;
      setGovernancePending({ checked: true, shouldWait: false });
      return () => {
        cancelled = true;
      };
    }

    if (!roleAppAccess.checked) {
      setGovernancePending({ checked: false, shouldWait: false });
      return () => {
        cancelled = true;
      };
    }

    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    if (isGlobalSuperadminForSession(bindings, session.user)) {
      govPendingLoadedKeyRef.current = `${session.user.id}:${govBundleRevision}`;
      setGovernancePending({ checked: true, shouldWait: false });
      return () => {
        cancelled = true;
      };
    }

    const pendingKey = `${session.user.id}:${govBundleRevision}`;
    if (govPendingLoadedKeyRef.current === pendingKey) {
      return () => {
        cancelled = true;
      };
    }

    setGovernancePending({ checked: false, shouldWait: false });
    (async () => {
      const res = await loadGovernancePendingWaitRemote({ force: true });
      if (cancelled) return;
      govPendingLoadedKeyRef.current = pendingKey;
      setGovernancePending({
        checked: true,
        shouldWait: Boolean(res?.ok && res.shouldWait),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [
    authChecked,
    session?.user?.id,
    roleAppAccess.checked,
    govBundleRevision,
  ]);

  // ------------------------------------------------------------
  // Trainer App Access (restrict apps for trainer logins)
  // ------------------------------------------------------------
  const [trainerAppAccess, setTrainerAppAccess] = useState(() => ({
    checked: false,
    isTrainerLogin: false,
    allowedAppKeys: null,
    trainerRow: null,
  }));

  useEffect(() => {
    let cancelled = false;
    if (!authChecked)
      return () => {
        cancelled = true;
      };
    if (!roleAppAccess.checked)
      return () => {
        cancelled = true;
      };

    const userId = session?.user?.id;
    const email = resolveSessionPrimaryEmail(session);
    if (!session || !email || !userId) {
      setTrainerAppAccess({
        checked: true,
        isTrainerLogin: false,
        allowedAppKeys: null,
        trainerRow: null,
      });
      return () => {
        cancelled = true;
      };
    }

    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    const entitlements = Array.isArray(roleAppAccess.entitlements)
      ? roleAppAccess.entitlements
      : [];
    const skipTrainerProbe =
      route === "access-pending" ||
      __postRegistrationAccessPendingActive() ||
      (governancePending.checked && governancePending.shouldWait) ||
      (!roleAppAccess.isGlobalSuperAdmin &&
        !hasActiveTenantRoleBindings(bindings) &&
        entitlements.length === 0);

    if (skipTrainerProbe) {
      trainerLoadedUserIdRef.current = userId;
      setTrainerAppAccess({
        checked: true,
        isTrainerLogin: false,
        allowedAppKeys: null,
        trainerRow: null,
      });
      return () => {
        cancelled = true;
      };
    }

    if (trainerLoadedUserIdRef.current === userId) {
      return () => {
        cancelled = true;
      };
    }

    setTrainerAppAccess((p) => ({ ...p, checked: false }));
    (async () => {
      const trId = `trainer-email-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mTs = `tm-loadTrainerByLogin-start-${trId}`;
      const mTe = `tm-loadTrainerByLogin-end-${trId}`;
      try {
        if (devPerfEnabled()) devMark(mTs);
        const r = await __promiseWithTimeout(
          loadTrainerByLoginEmail(email),
          TRAINER_ACCESS_TIMEOUT_MS,
          null,
        );
        if (devPerfEnabled()) {
          devMark(mTe);
          devMeasure(`tm-loadTrainerByLogin-${trId}`, mTs, mTe);
        }
        if (cancelled) return;
        trainerLoadedUserIdRef.current = userId;
        if (!r) {
          setTrainerAppAccess({
            checked: true,
            isTrainerLogin: false,
            allowedAppKeys: null,
            trainerRow: null,
          });
          return;
        }
        const trainer = r?.ok ? r.data || null : null;
        const isTrainerLogin = !!(trainer && __asStr(trainer?.login_email));
        if (!isTrainerLogin) {
          setTrainerAppAccess({
            checked: true,
            isTrainerLogin: false,
            allowedAppKeys: null,
            trainerRow: null,
          });
          return;
        }
        const allowed = new Set();
        allowed.add(normalizeGovernanceAppKey("trainerstunden"));
        allowed.add(normalizeGovernanceAppKey("trainer"));
        if (trainer?.can_access_schleifchen)
          allowed.add(normalizeGovernanceAppKey("matchmixer"));
        for (const e of roleAppAccess.entitlements || []) {
          const app = normalizeGovernanceAppKey(__asStr(e?.app_key));
          if (!app) continue;
          if (app === "matchmixer") {
            if (trainer?.can_access_schleifchen) allowed.add("matchmixer");
            continue;
          }
          allowed.add(app);
        }
        setTrainerAppAccess({
          checked: true,
          isTrainerLogin: true,
          allowedAppKeys: Array.from(allowed),
          trainerRow: trainer,
        });
      } catch (err) {
        if (cancelled) return;
        if (!__isAbortError(err)) {
          console.error(
            "[reactApp] Failed to load trainer app access state:",
            err,
          );
        }
        trainerLoadedUserIdRef.current = userId;
        setTrainerAppAccess({
          checked: true,
          isTrainerLogin: false,
          allowedAppKeys: null,
          trainerRow: null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authChecked,
    session?.user?.id,
    roleAppAccess.checked,
    roleAppAccess.isGlobalSuperAdmin,
    roleAppAccess.bindings,
    roleAppAccess.entitlements,
    route,
    governancePending.checked,
    governancePending.shouldWait,
  ]);

  const coachDeskSelfProfileHref = useMemo(() => {
    if (!trainerAppAccess.checked) return "";
    const trainerRow = trainerAppAccess.trainerRow;
    const trainerId = __asStr(trainerRow?.id);
    if (!trainerId && !trainerAppAccess.isTrainerLogin) return "";
    return resolveCoachDeskSelfProfileMenuHref({
      trainerRow,
      bindings: roleAppAccess.bindings,
      trainerId,
      mode: trainerAppAccess.isTrainerLogin ? "trainer" : "",
    });
  }, [
    trainerAppAccess.checked,
    trainerAppAccess.isTrainerLogin,
    trainerAppAccess.trainerRow,
    roleAppAccess.bindings,
  ]);

  const effectiveVisibleAppKeys = useMemo(() => {
    if (!roleAppAccess.checked) return new Set();
    if (!accessManifestsState.loaded) return new Set();

    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    const entitlements = Array.isArray(roleAppAccess.entitlements)
      ? roleAppAccess.entitlements
      : [];
    const roleAppAccessRows = Array.isArray(roleAppAccess.roleAppAccessRows)
      ? roleAppAccess.roleAppAccessRows
      : [];

    const tenantScopeIds = Array.from(
      new Set(
        bindings
          .filter((b) => String(b?.scope || "").toLowerCase() === "tenant")
          .map((b) => String(b?.scope_id || "").trim())
          .filter(Boolean),
      ),
    );
    const hasGlobalSuperadmin =
      Boolean(roleAppAccess.isGlobalSuperAdmin) ||
      isGlobalSuperadminForSession(bindings, session?.user);

    const out = new Set();
    const featureEnabled = (appKey) => {
      const k = String(appKey || "").trim();
      const entry = getAppCatalogEntry(k);
      const flagKey = entry?.featureFlagKey || k;
      return (
        !areFeatureFlagsReadySync() || isFeatureEnabledSync(flagKey) !== false
      );
    };

    if (hasGlobalSuperadmin) {
      const effectiveGlobal = getEffectiveVisibleAppsForScope({
        scope: "global",
        scopeId: "global",
        appManifests: accessManifestsState.manifests,
        isManifestsLoaded: accessManifestsState.loaded,
        roleBindings: bindings,
        entitlements,
        roleAppAccessRows,
        isFeatureEnabled: featureEnabled,
        authUser: session?.user,
      });
      for (const key of effectiveGlobal?.effectiveVisibleAppKeys || [])
        out.add(key);
      return restrictEffectiveVisibleAppsForFamilyOnly(out, bindings);
    }

    for (const sid of tenantScopeIds) {
      const effective = getEffectiveVisibleAppsForScope({
        scope: "tenant",
        scopeId: sid,
        appManifests: accessManifestsState.manifests,
        isManifestsLoaded: accessManifestsState.loaded,
        roleBindings: bindings,
        entitlements,
        roleAppAccessRows,
        isFeatureEnabled: featureEnabled,
        authUser: session?.user,
      });
      for (const key of effective?.effectiveVisibleAppKeys || []) out.add(key);
    }

    return restrictEffectiveVisibleAppsForFamilyOnly(out, bindings);
  }, [
    roleAppAccess.checked,
    roleAppAccess.isGlobalSuperAdmin,
    roleAppAccess.bindings,
    roleAppAccess.entitlements,
    roleAppAccess.roleAppAccessRows,
    accessManifestsState.loaded,
    accessManifestsState.manifests,
    session?.user,
  ]);

  const primaryTenantIdForSubscription = useMemo(() => {
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    for (const b of bindings) {
      if (String(b?.scope || "").toLowerCase() !== "tenant") continue;
      const sid = String(b?.scope_id || "").trim();
      if (sid) return sid;
    }
    return "";
  }, [roleAppAccess.bindings]);

  const [tenantSubscriptionAccess, setTenantSubscriptionAccess] = useState(() =>
    resolveSubscriptionAccess(null),
  );

  useEffect(() => {
    let cancelled = false;
    const tid = primaryTenantIdForSubscription;
    if (!tid) {
      setTenantSubscriptionAccess(resolveSubscriptionAccess(null));
      return undefined;
    }
    (async () => {
      const result = await ensureTenantSubscription(tid);
      if (cancelled) return;
      if (result.ok) {
        setTenantSubscriptionAccess(resolveSubscriptionAccess(result.data));
      }
    })();

    const onSubscriptionChanged = (event) => {
      const detail = event?.detail || {};
      const eventTid = String(detail.tenantId || "").trim();
      if (eventTid && eventTid !== tid) return;
      if (detail.subscription) {
        setTenantSubscriptionAccess(
          resolveSubscriptionAccess(detail.subscription),
        );
        return;
      }
      void ensureTenantSubscription(tid).then((result) => {
        if (!result.ok) return;
        setTenantSubscriptionAccess(resolveSubscriptionAccess(result.data));
      });
    };
    try {
      window.addEventListener(
        TENANT_SUBSCRIPTION_CHANGED_EVENT,
        onSubscriptionChanged,
      );
    } catch {
      /* noop */
    }
    return () => {
      cancelled = true;
      try {
        window.removeEventListener(
          TENANT_SUBSCRIPTION_CHANGED_EVENT,
          onSubscriptionChanged,
        );
      } catch {
        /* noop */
      }
    };
  }, [primaryTenantIdForSubscription]);

  useEffect(() => {
    if (effectiveVisibleAppKeys.size > 0) {
      try {
        sessionStorage.removeItem("tm_skip_access_gate_until");
      } catch (_) {}
    }
  }, [effectiveVisibleAppKeys.size]);

  /** Globaler Superadmin: Feature-Selection überspringen; Launcher folgt effectiveVisible (Governance). */
  const launcherIsSuperAdmin = useMemo(() => {
    if (rpcSuperadminResolved === true) return true;
    if (sessionSuperadminJwt) return true;
    if (roleAppAccess.isGlobalSuperAdmin) return true;
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    return isGlobalSuperadminForSession(bindings, session?.user);
  }, [
    rpcSuperadminResolved,
    sessionSuperadminJwt,
    roleAppAccess.isGlobalSuperAdmin,
    roleAppAccess.bindings,
    session?.user,
  ]);

  const applyTrainerLauncherAppRestriction =
    trainerAppAccess.isTrainerLogin && !launcherIsSuperAdmin;

  const isFamilyOnlyLogin = useMemo(() => {
    if (!roleAppAccess.checked) return false;
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    return isFamilyOnlyRoleBindings(bindings);
  }, [roleAppAccess.checked, roleAppAccess.bindings]);

  const isTeamCaptainOnlyLogin = useMemo(() => {
    if (!roleAppAccess.checked) return false;
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    return isTeamCaptainOnlyRoleBindings(bindings);
  }, [roleAppAccess.checked, roleAppAccess.bindings]);

  const resolvedHomeRoute = useMemo(() => {
    const allowed = Array.from(effectiveVisibleAppKeys || []);
    const effectiveAllowed = applyTrainerLauncherAppRestriction
      ? allowed.filter((k) =>
          __trainerAllowsAppKey(trainerAppAccess.allowedAppKeys, k),
        )
      : allowed;
    return resolveHomeRoute({
      preferredHomeApp,
      allowedAppKeys: effectiveAllowed,
      isFamilyOnlyLogin,
      isTeamCaptainOnlyLogin,
    });
  }, [
    effectiveVisibleAppKeys,
    preferredHomeApp,
    applyTrainerLauncherAppRestriction,
    trainerAppAccess.allowedAppKeys,
    isFamilyOnlyLogin,
    isTeamCaptainOnlyLogin,
  ]);

  const effectiveHomeAllowedAppKeys = useMemo(() => {
    const allowed = Array.from(effectiveVisibleAppKeys || []);
    const effectiveAllowed = applyTrainerLauncherAppRestriction
      ? allowed.filter((k) =>
          __trainerAllowsAppKey(trainerAppAccess.allowedAppKeys, k),
        )
      : allowed;
    return new Set(
      effectiveAllowed.map((k) => normalizeGovernanceAppKey(k)).filter(Boolean),
    );
  }, [
    effectiveVisibleAppKeys,
    applyTrainerLauncherAppRestriction,
    trainerAppAccess.allowedAppKeys,
  ]);

  const launcherAccessReady = Boolean(
    session &&
    roleAppAccess.checked &&
    accessManifestsState.loaded &&
    trainerAppAccess.checked &&
    __governanceBindingsReady(roleAppAccess, session),
  );

  const dynamicAppLoaderAccessReady =
    launcherAccessReady ||
    route === "trainingsdesk-invite" ||
    route === "matchflow-accept";

  // Für den Hub („Deine Tenniswelt") gilt eine relaxtere Ready-Definition:
  // Brandneue Nutzer ohne Vereinsbindings sind dort ausdrücklich willkommen
  // (Onboarding-Hero übernimmt). Daher hier KEIN hasActiveTenantRoleBindings-
  // Check – sonst hängt der Hub ewig im „Laden …"-Zustand und der AppLauncher
  // im Shell-Chrome darf trotzdem klickbar bleiben (Apps sind ohnehin offen,
  // nur ClubHub bleibt rollenbasiert – siehe useAppLauncherState).
  // Superadmin-RPC muss bekannt sein, sonst strippt der Abo-Filter Club-Apps
  // fälschlich auf nur TrainingsDesk, bevor is_superadmin antwortet.
  const hubAccessReady = Boolean(
    session &&
    roleAppAccess.checked &&
    accessManifestsState.loaded &&
    trainerAppAccess.checked &&
    (sessionSuperadminJwt ||
      roleAppAccess.isGlobalSuperAdmin ||
      isGlobalSuperadminForSession(
        Array.isArray(roleAppAccess.bindings) ? roleAppAccess.bindings : [],
        session?.user,
      ) ||
      rpcSuperadminResolved !== null),
  );

  const canUseBallmatesKi = useMemo(
    () =>
      hasBallmatesKiAccessFromEntitlements(roleAppAccess.entitlements, {
        isGlobalSuperadmin: Boolean(roleAppAccess.isGlobalSuperAdmin),
      }),
    [roleAppAccess.entitlements, roleAppAccess.isGlobalSuperAdmin],
  );

  const subscriptionScopedVisibleAppKeys = useMemo(() => {
    const scoped = applySubscriptionToLauncherAppKeys(
      effectiveVisibleAppKeys,
      tenantSubscriptionAccess,
      { isSuperAdmin: launcherIsSuperAdmin },
    );
    // Free/Starter: Club-Apps weg, TrainingsDesk-Kern bleibt immer sichtbar,
    // wenn Superadmin, Trainer-Login oder Governance die App schon kennt.
    const hadCore = [...effectiveVisibleAppKeys].some((k) =>
      isSubscriptionCoreAppKey(k),
    );
    if (launcherIsSuperAdmin || trainerAppAccess.isTrainerLogin || hadCore) {
      scoped.add("trainerstunden");
    }
    return scoped;
  }, [
    effectiveVisibleAppKeys,
    tenantSubscriptionAccess,
    launcherIsSuperAdmin,
    trainerAppAccess.isTrainerLogin,
  ]);

  const canAccessCurrentRouteApp = useMemo(
    () =>
      canAccessRouteApp(route, {
        effectiveVisibleAppKeys: subscriptionScopedVisibleAppKeys,
        isGlobalSuperAdmin: launcherIsSuperAdmin,
        accessReady: launcherAccessReady,
        isTrainerLogin: trainerAppAccess.isTrainerLogin,
        trainerAllowedAppKeys: applySubscriptionToLauncherAppKeys(
          trainerAppAccess.allowedAppKeys,
          tenantSubscriptionAccess,
          { isSuperAdmin: launcherIsSuperAdmin },
        ),
      }),
    [
      route,
      subscriptionScopedVisibleAppKeys,
      launcherIsSuperAdmin,
      launcherAccessReady,
      trainerAppAccess.isTrainerLogin,
      trainerAppAccess.allowedAppKeys,
      tenantSubscriptionAccess,
    ],
  );

  /** Effektive Launcher-/Hub-Apps (Manifest global∧tenant ∩ Matrix) — geräteunabhängig. */
  const effectiveLauncherAllowedAppKeys = useMemo(() => {
    if (!hubAccessReady) return null;

    const out = new Set();
    for (const k of subscriptionScopedVisibleAppKeys) {
      const nk = normalizeGovernanceAppKey(k);
      if (!nk) continue;
      if (areFeatureFlagsReadySync() && isFeatureEnabledSync(nk) === false)
        continue;
      if (
        applyTrainerLauncherAppRestriction &&
        !__trainerAllowsAppKey(
          applySubscriptionToLauncherAppKeys(
            trainerAppAccess.allowedAppKeys,
            tenantSubscriptionAccess,
            { isSuperAdmin: launcherIsSuperAdmin },
          ),
          nk,
        )
      )
        continue;
      out.add(nk);
    }

    return out;
  }, [
    hubAccessReady,
    subscriptionScopedVisibleAppKeys,
    applyTrainerLauncherAppRestriction,
    trainerAppAccess.allowedAppKeys,
    tenantSubscriptionAccess,
    launcherIsSuperAdmin,
  ]);

  const launcherAllowedAppKeys = useMemo(() => {
    if (!launcherAccessReady) return new Set();
    return effectiveLauncherAllowedAppKeys instanceof Set
      ? effectiveLauncherAllowedAppKeys
      : new Set();
  }, [launcherAccessReady, effectiveLauncherAllowedAppKeys]);

  /** Ausstehende Zugriffsanfrage ohne App-Rechte: nur Abmelden im Shell-Chrome. */
  const shellAccessRestricted = useMemo(() => {
    if (!session?.user?.id || launcherIsSuperAdmin) return false;
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    const hasAppRights =
      roleAppAccess.checked &&
      (effectiveVisibleAppKeys.size > 0 ||
        hasActiveTenantRoleBindings(bindings));
    if (hasAppRights) return false;
    if (__postRegistrationAccessPendingActive()) return true;
    if (!governancePending.checked) return false;
    return Boolean(governancePending.shouldWait);
  }, [
    session?.user?.id,
    launcherIsSuperAdmin,
    roleAppAccess.checked,
    roleAppAccess.bindings,
    effectiveVisibleAppKeys,
    governancePending.checked,
    governancePending.shouldWait,
  ]);

  const launchAccessContext = useMemo(
    () =>
      __launchAccessContext(
        session,
        launcherIsSuperAdmin,
        sessionSuperadminJwt,
        rpcSuperadminResolved,
      ),
    [
      session,
      launcherIsSuperAdmin,
      sessionSuperadminJwt,
      rpcSuperadminResolved,
    ],
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!launcherAccessReady || !session?.user?.id) return;
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    const activeRoles = bindings.map(
      (b) =>
        `${String(b?.scope || "").trim()}:${String(b?.scope_id || "").trim()}:${String(b?.role || "").trim()}`,
    );
    const derivedApps =
      deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
        roleAppAccess.bindings,
        roleAppAccess.entitlements,
        roleAppAccess.roleAppAccessRows,
        session?.user,
      );
    const activeApps = derivedApps?.allApps
      ? "(all)"
      : Array.from(
          effectiveVisibleAppKeys || derivedApps?.allowedAppKeys || [],
        );
    const allowedApps = launcherIsSuperAdmin
      ? "(all)"
      : Array.from(launcherAllowedAppKeys || []);
    const effectiveApps = Array.from(effectiveVisibleAppKeys || []);
    const email = resolveSessionPrimaryEmail(session);
    const hasAppRightsDiag = effectiveVisibleAppKeys.size > 0;
    // Diagnose-Label für den Zugriffsgate (ehem. "featureSelectionGate", die
    // gleichnamige Page existiert nicht mehr; Default-Ziel ist jetzt der Hub).
    let accessGate = "n/a";
    if (session) {
      if (!roleAppAccess.checked) accessGate = "await_governance_bundle";
      else if (!governancePending.checked) accessGate = "await_pending_wait";
      else if (launcherIsSuperAdmin) accessGate = "skip_superadmin";
      else if (governancePending.shouldWait)
        accessGate = "would_redirect_access_pending";
      else if (!hasAppRightsDiag) accessGate = "would_land_on_hub_onboarding";
      else accessGate = "has_app_access";
    }
    // eslint-disable-next-line no-console
    console.debug("[tm-access-diag] reactApp", {
      userId: session.user.id,
      email,
      isSuperAdmin: launcherIsSuperAdmin,
      activeRoles,
      activeApps,
      allowedApps,
      effectiveApps,
      governanceBindingsReady: __governanceBindingsReady(
        roleAppAccess,
        session,
      ),
      identityRelink: roleAppAccess.identityRelink,
      accessGate,
      trainerAppAccess,
      effectiveVisibleAppKeys: Array.from(effectiveVisibleAppKeys),
    });
  }, [
    launcherAccessReady,
    session,
    launcherIsSuperAdmin,
    roleAppAccess.checked,
    roleAppAccess.bindings,
    roleAppAccess.entitlements,
    roleAppAccess.roleAppAccessRows,
    roleAppAccess.identityRelink,
    trainerAppAccess,
    governancePending.checked,
    governancePending.shouldWait,
    effectiveVisibleAppKeys,
    launcherAllowedAppKeys,
  ]);

  // Route guard: alle App-Routen inkl. Unterseiten (z. B. matchmixer-setup).
  useEffect(() => {
    if (!authChecked || !session) return;
    if (!launcherAccessReady) return;

    const appKey = __appKeyForRoute(route);
    if (!appKey) {
      if (route === "access-requests-admin") {
        if (
          !canAccessAccessRequestsAdmin(roleAppAccess.bindings, session?.user)
        ) {
          navigateTo("hub", true);
        }
      }
      return;
    }

    if (canAccessCurrentRouteApp) return;

    const resolvedAppKey = __asStr(appKey).toLowerCase();
    // TrainingsDesk-Kern nie wegredirecten (Free/Starter lädt die App).
    if (
      resolvedAppKey === "trainerstunden" ||
      resolvedAppKey === "trainingsdesk" ||
      resolvedAppKey === "coachdesk"
    ) {
      return;
    }

    if (__routingDebugEnabled()) {
      console.debug("[tm routing-guard] redirect: app route not allowed", {
        route,
        resolvedAppKey: appKey,
        effectiveVisibleAppKeys: Array.from(effectiveVisibleAppKeys),
        resolvedHomeRoute,
        launcherIsSuperAdmin,
      });
    }
    navigateTo(resolvedHomeRoute || "hub", true);
  }, [
    authChecked,
    session,
    route,
    launcherAccessReady,
    canAccessCurrentRouteApp,
    effectiveVisibleAppKeys,
    resolvedHomeRoute,
    launcherIsSuperAdmin,
    roleAppAccess.bindings,
  ]);

  useEffect(() => {
    if (!authChecked || !session) return;
    if (!isGlobalSuperadminOnlyRoute(route)) return;
    if (!roleAppAccess.checked) return;
    if (launcherIsSuperAdmin) return;
    navigateTo(resolvedHomeRoute || "hub", true);
  }, [
    authChecked,
    session,
    route,
    roleAppAccess.checked,
    launcherIsSuperAdmin,
    resolvedHomeRoute,
  ]);

  useEffect(() => {
    if (!authChecked || !session) return;
    const appKey = __asStr(__appKeyForRoute(route)).toLowerCase();
    if (!appKey) return;
    trackAppOpen(appKey);
  }, [authChecked, session?.user?.id, route]);

  const isPublicRoute = (() => {
    if (
      route === "login" ||
      route === "reset-password" ||
      route === "auth-callback" ||
      route === "share" ||
      route === "accept-invite" ||
      route === "trainingsdesk-invite" ||
      route === "matchflow-accept" ||
      route === "winterdoppel-join" ||
      route === "winterdoppel-me" ||
      route === "matchmixer-anmelden" ||
      route === "matchmixer-me" ||
      route === "winterdoppel-confirm-email" ||
      route === "winterdoppel-schedule"
    )
      return true;
    if (route === "datenschutz" || route === "agb" || route === "impressum")
      return true;
    if (typeof route === "string" && route.startsWith("public-")) return true;
    // Public share-by-code: player matches (read-only) should be accessible without login.
    if (route === "player-matches") {
      const code = __getQueryParam("code");
      if (code) return true;
    }
    // Allow share-followup routes without login (capability-based, per-tab).
    try {
      const raw = sessionStorage.getItem("ttb_public_routes") || "";
      const allowed = new Set(
        String(raw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      );
      return allowed.has(route);
    } catch (_) {
      return false;
    }
  })();

  useLayoutEffect(() => {
    if (!authChecked) return;
    syncPasswordRecoveryIntentFromBrowserUrl();
    clearStalePasswordRecoveryIntent(session);
    if (!session) return;
    if (!shouldForcePasswordResetFlow(session)) return;
    if (route === "reset-password") return;
    navigateTo("reset-password", true);
  }, [authChecked, session, route]);

  useLayoutEffect(() => {
    const finishHomeBootstrap = () => {
      initialHomeBootstrapDoneRef.current = true;
      setHomeBootstrapDone(true);
    };

    if (!authChecked || !session) return;
    if (initialHomeBootstrapDoneRef.current) return;
    if (!homePreferenceLoaded || !hubAccessReady) return;

    const entryRoute = __asStr(route);
    if (entryRoute !== "hub" && entryRoute !== "home") {
      finishHomeBootstrap();
      return;
    }

    const storedPreferred = readStoredPreferredHomeAppSync();
    const preferred = normalizePreferredHomeApp(
      preferredHomeApp !== "hub" ? preferredHomeApp : storedPreferred,
    );
    if (!canUsePreferredHomeApp(preferred)) {
      if (
        canUsePreferredHomeApp(storedPreferred) &&
        preferredHomeApp === "hub"
      ) {
        return;
      }
      finishHomeBootstrap();
      return;
    }

    const preferredKey = normalizeGovernanceAppKey(preferred);
    if (!effectiveHomeAllowedAppKeys.has(preferredKey)) {
      if (effectiveVisibleAppKeys.size === 0) return;
      finishHomeBootstrap();
      return;
    }

    if (preferredKey !== normalizeGovernanceAppKey(entryRoute)) {
      navigateTo(preferred, true);
    }
    finishHomeBootstrap();
  }, [
    authChecked,
    session,
    route,
    homePreferenceLoaded,
    hubAccessReady,
    preferredHomeApp,
    effectiveHomeAllowedAppKeys,
    effectiveVisibleAppKeys,
  ]);

  useEffect(() => {
    if (!authChecked) return;
    if (!session && !isPublicRoute && !authBootstrapError) {
      navigateTo(
        shouldForcePasswordResetFlow(session) ? "reset-password" : "login",
        true,
      );
      return;
    }
    if (session && (route === "login" || route === "auth-callback")) {
      syncPasswordRecoveryIntentFromBrowserUrl();
      if (shouldForcePasswordResetFlow(session)) {
        navigateTo("reset-password", true);
        return;
      }
      const rawNext = __getQueryParam("next");
      const decoded = __decodeURIComponentSafe(rawNext);
      const candidate =
        decoded && isSafeRelativeInternalNextPath(decoded)
          ? decoded
          : rawNext && isSafeRelativeInternalNextPath(rawNext)
            ? rawNext
            : "";
      if (candidate && isInviteAcceptIntentNextPath(candidate)) {
        const pathForRouter = candidate.startsWith("/")
          ? candidate.slice(1)
          : candidate;
        navigateTo(pathForRouter, true);
        return;
      }
      if (route === "auth-callback" && candidate) {
        const pathForRouter = candidate.startsWith("/")
          ? candidate.slice(1)
          : candidate;
        navigateTo(pathForRouter, true);
        return;
      }
      // Sofort zur Sync-/State-Präferenz – kein Zwischenstopp auf dem Hub.
      {
        const syncPreferred = readStoredPreferredHomeAppSync();
        const preferredNow = canUsePreferredHomeApp(preferredHomeApp)
          ? preferredHomeApp
          : syncPreferred;
        if (canUsePreferredHomeApp(preferredNow)) {
          navigateTo(preferredNow, true);
          return;
        }
      }
      // Superadmin: Access-Pending überspringen, Home ohne Präferenz → Hub.
      if (
        launcherIsSuperAdmin ||
        sessionSuperadminJwt ||
        rpcSuperadminResolved === true
      ) {
        if (!homePreferenceLoaded || !hubAccessReady) return;
        navigateTo(resolvedHomeRoute, true);
        return;
      }
      if (rpcSuperadminResolved === null) {
        return;
      }
      if (
        !roleAppAccess.checked ||
        !governancePending.checked ||
        !homePreferenceLoaded ||
        !launcherAccessReady
      ) {
        return;
      }
      const authBindings = Array.isArray(roleAppAccess.bindings)
        ? roleAppAccess.bindings
        : [];
      const authHasAppRights =
        effectiveVisibleAppKeys.size > 0 ||
        hasActiveTenantRoleBindings(authBindings);
      if (
        !authHasAppRights &&
        !__invitePostAcceptGateActive() &&
        (governancePending.shouldWait ||
          __postRegistrationAccessPendingActive())
      ) {
        navigateTo("access-pending", true);
        return;
      }
      navigateTo(resolvedHomeRoute, true);
      return;
    }
    if (session && route === "home") {
      {
        const syncPreferred = readStoredPreferredHomeAppSync();
        const preferredNow = canUsePreferredHomeApp(preferredHomeApp)
          ? preferredHomeApp
          : syncPreferred;
        if (canUsePreferredHomeApp(preferredNow)) {
          navigateTo(preferredNow, true);
          return;
        }
      }
      if (
        launcherIsSuperAdmin ||
        sessionSuperadminJwt ||
        rpcSuperadminResolved === true
      ) {
        if (!homePreferenceLoaded || !hubAccessReady) return;
        navigateTo(resolvedHomeRoute, true);
        return;
      }
      if (!homePreferenceLoaded || !launcherAccessReady) return;
      navigateTo(resolvedHomeRoute, true);
      return;
    }

    if (!session) return;

    const launch = __launchAccessContext(
      session,
      launcherIsSuperAdmin,
      sessionSuperadminJwt,
      rpcSuperadminResolved,
    );
    if (launch.knownSuperadmin) {
      __clearPostRegistrationAccessPending();
    }

    if (launch.knownSuperadmin && route === "access-pending") {
      navigateTo("hub", true);
      return;
    }

    const isPublicPrefixEarly =
      typeof route === "string" && route.startsWith("public-");
    const isAuthFlowEarly =
      route === "auth-callback" ||
      route === "accept-invite" ||
      route === "trainingsdesk-invite" ||
      route === "matchflow-accept" ||
      route === "reset-password";

    // Schnellpfad: frisch registriert / Warte-Screen — ohne Manifest- oder Trainer-Gates
    if (
      !launch.knownSuperadmin &&
      !__superadminIdentityPending(launch) &&
      __postRegistrationAccessPendingActive() &&
      route !== "access-pending" &&
      route !== "login" &&
      route !== "reset-password" &&
      !isAuthFlowEarly &&
      !isPublicPrefixEarly &&
      !__mayRemainOnPlatformVerwaltungRoute(
        route,
        roleAppAccess.bindings,
        session,
      ) &&
      !__hubBlocksAccessGateRedirect(route, launch)
    ) {
      navigateTo("access-pending", true);
      return;
    }

    const bindingsEarly = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    const hasAppRightsEarly =
      roleAppAccess.checked &&
      (effectiveVisibleAppKeys.size > 0 ||
        hasActiveTenantRoleBindings(bindingsEarly));

    if (
      !launch.knownSuperadmin &&
      !__superadminIdentityPending(launch) &&
      roleAppAccess.checked &&
      governancePending.checked &&
      governancePending.shouldWait &&
      !hasAppRightsEarly &&
      !__invitePostAcceptGateActive() &&
      route !== "access-pending" &&
      route !== "login" &&
      route !== "reset-password" &&
      !isAuthFlowEarly &&
      !isPublicPrefixEarly &&
      !__mayRemainOnPlatformVerwaltungRoute(route, bindingsEarly, session) &&
      !__hubBlocksAccessGateRedirect(route, launch)
    ) {
      navigateTo("access-pending", true);
      return;
    }

    if (route === "access-pending") {
      if (
        !launcherIsSuperAdmin &&
        !hasAppRightsEarly &&
        __invitePostAcceptGateActive()
      ) {
        navigateTo("hub", true);
        return;
      }
      if (
        !hasAppRightsEarly &&
        (__postRegistrationAccessPendingActive() ||
          !governancePending.checked ||
          governancePending.shouldWait)
      ) {
        return;
      }
      if (
        governancePending.checked &&
        !governancePending.shouldWait &&
        !__postRegistrationAccessPendingActive()
      ) {
        __clearPostRegistrationAccessPending();
        // Nach Pending-Klärung: bei vorhandenen Rechten in die Home-App,
        // sonst direkt in den Hub (Onboarding-Hero übernimmt).
        navigateTo(hasAppRightsEarly ? resolvedHomeRoute : "hub", true);
      }
      return;
    }

    const readyForLaunch =
      roleAppAccess.checked &&
      governancePending.checked &&
      accessManifestsState.loaded &&
      trainerAppAccess.checked;
    if (!readyForLaunch) return;

    const bindings = bindingsEarly;
    const hasAppRights = hasAppRightsEarly;
    const isAuthFlow = isAuthFlowEarly;
    const isPublicPrefix = isPublicPrefixEarly;

    // Pending-Warte-Screen nur, solange der Nutzer noch keine sichtbaren App-Rechte hat (Schritt 1).
    if (
      !launch.knownSuperadmin &&
      !__superadminIdentityPending(launch) &&
      governancePending.shouldWait &&
      !hasAppRights &&
      !__invitePostAcceptGateActive()
    ) {
      if (
        !isAuthFlow &&
        !isPublicPrefix &&
        route !== "access-pending" &&
        route !== "login" &&
        route !== "reset-password" &&
        !__mayRemainOnPlatformVerwaltungRoute(route, bindings, session) &&
        !__hubBlocksAccessGateRedirect(route, launch)
      ) {
        navigateTo("access-pending", true);
      }
      return;
    }

    if (
      isAuthFlow ||
      route === "login" ||
      route === "reset-password" ||
      isPublicPrefix
    )
      return;

    if (launcherIsSuperAdmin) return;

    // Hub-Plattform (inkl. Ballmates KI): nicht weiter umlenken, wenn Tenant-Rollen existieren.
    if (__isHubPlatformRoute(route) && hasActiveTenantRoleBindings(bindings)) {
      if (hasAppRights) hubPlatformAccessEstablishedRef.current = true;
      return;
    }

    if (__mayRemainOnPlatformVerwaltungRoute(route, bindings, session)) {
      return;
    }

    if (hasAppRights && __isHubPlatformRoute(route)) {
      hubPlatformAccessEstablishedRef.current = true;
    }

    if (
      !hasAppRights &&
      governancePending.checked &&
      !governancePending.shouldWait &&
      !__postRegistrationAccessPendingActive() &&
      route !== "access-pending" &&
      !__invitePostAcceptGateActive() &&
      !(
        __isHubPlatformRoute(route) &&
        hubPlatformAccessEstablishedRef.current &&
        hasActiveTenantRoleBindings(bindings)
      ) &&
      !__mayRemainOnPlatformVerwaltungRoute(route, bindings, session)
    ) {
      if (__hubBlocksAccessGateRedirect(route, launch)) return;
      // Nutzer ohne App-Rechte landen im Hub („Deine Tenniswelt") – dort
      // übernimmt der Onboarding-Hero die kontextbasierte Aktivierung von
      // Verein/Familie/Training/Trainer. Die frühere /feature-selection-Seite
      // wurde komplett entfernt; alle Apps sind direkt im Hub sichtbar.
      navigateTo("hub", true);
      return;
    }
  }, [
    authChecked,
    session,
    route,
    resolvedHomeRoute,
    roleAppAccess.checked,
    roleAppAccess.isGlobalSuperAdmin,
    roleAppAccess.bindings,
    launcherIsSuperAdmin,
    sessionSuperadminJwt,
    rpcSuperadminResolved,
    governancePending.checked,
    governancePending.shouldWait,
    effectiveVisibleAppKeys,
    accessManifestsState.loaded,
    trainerAppAccess.checked,
    homePreferenceLoaded,
    launcherAccessReady,
    hubAccessReady,
    resolvedHomeRoute,
    effectiveHomeAllowedAppKeys,
  ]);

  // ENTFERNT: "Ein-App-Nutzer: Hub-Übersicht überspringen, direkt in die App."
  //
  // Diese Auto-Redirect-Logik hat User mit nur einer App-Zuordnung (z.B. nur
  // `trainerstunden` als Player/Parent) sofort aus dem Hub in die App katapult-
  // iert. In Kombination mit dem ParentPortalGate, der Player-Sessions hart auf
  // `/trainingsdesk/family` fixiert, entstand eine echte Falle ohne Rückweg:
  // Der „Startseite"-Button im AppLauncher → navigateTo("hub") wurde durch
  // diesen Effekt sofort wieder zurück in die App geworfen.
  //
  // Neue Produktentscheidung: Der Hub ist immer das Zuhause. Apps werden
  // aktiv über Kacheln im Hub gestartet, nie automatisch.

  // IMPORTANT: From here on, do not early-return before running all hooks.
  // Manifest: nur wenn deferred breadcrumbApi geladen (kein Supabase im Initial-Load)
  const manifest = useMemo(() => {
    const r = __asStr(route);
    if (!r || !breadcrumbApi) return null;
    const appId = breadcrumbApi.findAppForRoute(route);
    if (!appId) return null;
    const apps = breadcrumbApi.getAllApps();
    const app = apps.find((a) => a.appId === appId);
    return app?.manifest || null;
  }, [route, breadcrumbApi]);

  const contentSlug = useMemo(() => {
    const slug = __asStr(route);
    if (!slug) return "";
    if (slug === "home" || slug === "player-matches") return "";
    if (
      slug === "login" ||
      slug === "reset-password" ||
      slug === "auth-callback"
    )
      return "";
    if (slug === "profile") return "";
    if (slug === "accept-invite") return "";
    if (slug === "trainingsdesk-invite") return "";
    if (slug === "matchflow-accept") return "";
    if (slug === "access-pending") return "";
    if (slug.startsWith("superadmin") || slug.endsWith("-admin")) return "";
    // Loader-Apps (MatchMixer, Club, …): Route gehört zum dynamischen App-Bundle, nicht zur CMS-ContentPage.
    // Wichtig auch wenn breadcrumbApi noch deferred ist — sonst slug z. B. "matchmixer-setup" → doppelter Seiteninhalt.
    if (resolveLoaderAppKeyFromRoute(slug)) return "";
    if (breadcrumbApi) {
      const appId = breadcrumbApi.findAppForRoute(slug);
      if (appId) return "";
    }
    if (slug === "settings") return "";
    // Verwaltungs-Routen rendern eigene Seiten (PeopleAdminPage, …) — kein CMS-ContentPage-Slug.
    if (
      slug === "admin-people" ||
      slug === "admin-people-detail" ||
      slug === "admin-locations" ||
      slug === "clubhub"
    )
      return "";
    return slug;
  }, [route, breadcrumbApi]);

  const fontSize = state?.shared?.a11y?.fontSize || "m";

  const breadcrumbRoute = useMemo(() => {
    // MatchMixer: Overview-Route ist "matchmixer", Flow-Schritte Setup/Spielplan/…
    // Map overview based on tournament STATUS from meta, not UI state (which can be stale).

    if (route === "matchmixer-setup-players") {
      return "matchmixer-setup";
    }

    // For round-config and entry routes, return them as-is (they will be matched by round number)
    if (route === "matchmixer-round-config" || route === "matchmixer-entry") {
      return route;
    }

    // If explicitly on schedule page, return schedule (for planned tournaments)
    if (route === "matchmixer-schedule") {
      return "matchmixer-schedule";
    }

    if (route === "matchmixer") {
      const mod = state?.modules?.schleifchen || {};
      const tournament = mod.tournament || {};
      const meta = tournament.meta || {};

      // Use status from meta (single source of truth from DB)
      const status = String(meta.phase || meta.status || "").toLowerCase();

      // If status is explicitly "finished", show finish breadcrumb
      if (status === "finished" || status === "finish") {
        return "matchmixer-finish";
      }

      // If status is "planned", show schedule breadcrumb
      if (status === "planned") {
        return "matchmixer-schedule";
      }

      // If no schedule, show setup
      const hasSchedule =
        Array.isArray(mod.schedule) && mod.schedule.length > 0;
      if (!hasSchedule) return "matchmixer-setup";

      // For running tournaments, determine which round we're on
      const ui = mod.ui || {};
      const currentRound = Number(ui.round || 1);
      const completedRound = Number(ui.completedRound || 0);

      // If we just completed a round, we're likely on round-config for the next round
      if (
        currentRound > completedRound &&
        currentRound === completedRound + 1
      ) {
        return "matchmixer-round-config";
      }

      // Otherwise, we're on entry for the current round
      return "matchmixer-entry";
    }
    return route;
  }, [route, state]);

  const shellIdentity = useMemo(() => {
    const r = String(route || "").trim();
    if (
      r === "access-pending" &&
      (launchAccessContext.knownSuperadmin ||
        __superadminIdentityPending(launchAccessContext))
    ) {
      return getShellAppIdentityFromRoute("hub");
    }
    return getShellAppIdentityFromRoute(r);
  }, [route, launchAccessContext]);

  useEffect(() => {
    document.title = resolveShellDocumentTitle(route, shellIdentity, t);
  }, [route, shellIdentity, t]);

  /* App-scope: route → data-app für Akzent/Ring (Katalog-appKey). */
  const scopeAppKey = useMemo(() => {
    const r = String(route || "").trim();
    if (r === "access-requests-admin") return "access-requests-admin";
    if (r === "profile") return "profile";
    return __asStr(resolveAppKeyFromRoute(r) || "").trim() || null;
  }, [route]);

  const shellIdentityResolved = useMemo(
    () => resolveShellAuthIdentity(session),
    [session],
  );
  const [profileAvatarOverride, setProfileAvatarOverride] = useState("");
  useEffect(() => {
    const uid = __asStr(session?.user?.id);
    if (!uid) {
      setProfileAvatarOverride("");
      return undefined;
    }
    let cancelled = false;
    loadMyProfile()
      .then((res) => {
        if (cancelled) return;
        const dbAvatar = __asStr(res?.profile?.avatarUrl);
        if (dbAvatar) setProfileAvatarOverride(dbAvatar);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);
  useEffect(() => {
    const onProfileUpdated = (ev) => {
      const next = __asStr(ev?.detail?.avatarUrl);
      setProfileAvatarOverride(next);
    };
    window.addEventListener("tm:my-profile-updated", onProfileUpdated);
    return () =>
      window.removeEventListener("tm:my-profile-updated", onProfileUpdated);
  }, []);
  const authUserLabel =
    __asStr(shellIdentityResolved.label) ||
    (session ? t("appTopbar.identityFallback") : "");
  const authUserAvatar =
    __asStr(profileAvatarOverride) || __asStr(shellIdentityResolved.avatarUrl);
  const authUserInitials = __asStr(shellIdentityResolved.initials);

  const shellUserClubs = useMyClubsWithLabels(
    session?.user?.id ? roleAppAccess?.bindings : null,
    t,
  );

  const goToHubHome = useCallback(() => {
    // Persönliche Start-App nur beim ersten Einstieg nach Login — nicht bei
    // bewusstem „Startseite“-Klick (Hub bleibt navigierbares Zuhause).
    initialHomeBootstrapDoneRef.current = true;
    setHomeBootstrapDone(true);
    navigateTo("hub", false);
  }, []);

  // Kein Hook: muss vor Early-Returns stehen und darf die Hook-Anzahl nicht ändern.
  // Nur anzeigen, wenn die App denselben Check erfüllt — sonst landet der Klick im Guard→Hub.
  const canSeeVereinsabo = (() => {
    const bindings = Array.isArray(roleAppAccess.bindings)
      ? roleAppAccess.bindings
      : [];
    if (isFamilyOnlyRoleBindings(bindings)) return false;
    const tenantRoles = [...collectActiveTenantRolesFromBindings(bindings)];
    const mode = trainerAppAccess.isTrainerLogin
      ? "trainer"
      : launcherIsSuperAdmin || tenantRoles.length
        ? "coordinator"
        : "";
    return canManageTenantSubscription({
      mode,
      trainerRow: trainerAppAccess.trainerRow,
      tenantRoles,
    });
  })();

  // Now it is safe to early-return (all hooks above have run in a stable order).
  if (authBootstrapError) {
    const bodyKey =
      authBootstrapError === "timeout"
        ? "auth.bootstrap.blockedBodyTimeout"
        : "auth.bootstrap.blockedBodyFailed";
    const dbg = authDebugUrlEnabled ? getAuthDebugSnapshot() : null;
    const rpc = dbg?.lastRpcError;
    const rpcLine =
      rpc && (rpc.source || rpc.status !== "" || rpc.code || rpc.message)
        ? `${String(rpc.source || "")} | ${String(rpc.status ?? "")} | ${String(rpc.code ?? "")} | ${String(rpc.message || "")}`
        : "";
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? String(window.location.origin)
        : "";
    const pathname = String(
      location?.pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "") ||
        "",
    );
    const hashPresent =
      typeof window !== "undefined" &&
      String(window.location.hash || "").length > 0
        ? t("auth.debug.value.yes")
        : t("auth.debug.value.no");
    const sessionPresent = session
      ? t("auth.debug.value.yes")
      : t("auth.debug.value.no");
    const noneLabel = t("auth.debug.value.none");
    return (
      <section className="card">
        <h2 className="mb0">{t("auth.bootstrap.blockedTitle")}</h2>
        <p className="muted">{t(bodyKey)}</p>
        <div className="row row--wrap auth-debug-panel__actions">
          <button
            type="button"
            className="btn btn--solid"
            onClick={handleAuthBootstrapSessionRecheck}
          >
            {t("auth.debug.recheckCta")}
          </button>
          <button type="button" className="btn" onClick={onSignOut}>
            {t("auth.debug.signOutCta")}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => window.location.reload()}
          >
            {t("auth.bootstrap.reloadCta")}
          </button>
        </div>
        {authDebugUrlEnabled ? (
          <div className="auth-debug-panel">
            <h3 className="mb0">{t("auth.debug.boxTitle")}</h3>
            <dl className="auth-debug-panel__dl">
              <dt>{t("auth.debug.label.href")}</dt>
              <dd>{getSanitizedLocationHrefForDebug() || noneLabel}</dd>
              <dt>{t("auth.debug.label.origin")}</dt>
              <dd>{origin || noneLabel}</dd>
              <dt>{t("auth.debug.label.pathname")}</dt>
              <dd>{pathname || noneLabel}</dd>
              <dt>{t("auth.debug.label.hash")}</dt>
              <dd>{hashPresent}</dd>
              <dt>{t("auth.debug.label.session")}</dt>
              <dd>{sessionPresent}</dd>
              <dt>{t("auth.debug.label.userId")}</dt>
              <dd>{session?.user?.id ? String(session.user.id) : noneLabel}</dd>
              <dt>{t("auth.debug.label.email")}</dt>
              <dd>
                {session?.user?.email ? String(session.user.email) : noneLabel}
              </dd>
              <dt>{t("auth.debug.label.lastOk")}</dt>
              <dd>
                {dbg?.lastSuccessStep ? String(dbg.lastSuccessStep) : noneLabel}
              </dd>
              <dt>{t("auth.debug.label.lastFail")}</dt>
              <dd>
                {dbg?.lastFailedStep ? String(dbg.lastFailedStep) : noneLabel}
              </dd>
              <dt>{t("auth.debug.label.rpc")}</dt>
              <dd>{rpcLine || noneLabel}</dd>
              <dt>{t("auth.debug.label.timeoutMs")}</dt>
              <dd>
                {dbg?.bootstrapTimeoutMs != null &&
                Number.isFinite(Number(dbg.bootstrapTimeoutMs))
                  ? String(dbg.bootstrapTimeoutMs)
                  : noneLabel}
              </dd>
            </dl>
          </div>
        ) : null}
      </section>
    );
  }
  if (!authChecked) {
    return (
      <section className="card">
        <p className="muted">{t("common.loading")}</p>
      </section>
    );
  }
  if (!session && !isPublicRoute && !authBootstrapError) {
    // Redirect scheduled by effect above.
    return null;
  }

  // If we are about to normalize the route, render nothing for this tick.
  if (normalizedRoute && normalizedRoute !== route) {
    return null;
  }

  const isPublicPlayerMatches =
    !session && route === "player-matches" && !!__getQueryParam("code");

  const isSuperadminRoute =
    route === "superadmin" ||
    (typeof route === "string" &&
      (route.startsWith("superadmin") ||
        route === "content-admin" ||
        route === "i18n-admin" ||
        route === "form-admin"));

  const isHubRoute =
    route === "hub" ||
    route === "hub-app-detail" ||
    route === "tenniswelt" ||
    (typeof route === "string" && route.startsWith("hub-"));
  const fallback = <SuspenseLoaderToken />;

  // Hub nicht flashen, wenn eine Startseiten-App gesetzt ist und der
  // Bootstrap noch umleitet (oder gerade umleiten wird).
  const preferredHomeForHold = canUsePreferredHomeApp(preferredHomeApp)
    ? preferredHomeApp
    : readStoredPreferredHomeAppSync();
  const holdHubForPreferredHome =
    Boolean(session) &&
    route === "hub" &&
    canUsePreferredHomeApp(preferredHomeForHold) &&
    (!homePreferenceLoaded || !hubAccessReady || !homeBootstrapDone);

  const isMatchmixerScope = scopeAppKey === "matchmixer";
  const isClubmeisterschaftScope = scopeAppKey === "clubmeisterschaft";
  const content = isHubRoute ? (
    <Suspense fallback={fallback}>
      {route === "hub" ? (
        holdHubForPreferredHome ? (
          // Sichtbarer Platzhalter — SuspenseLoaderToken rendert null und wirkt
          // mit SideNav wie „leere rechte Spalte“, wenn das Overlay verzögert ist.
          <div className="ph-loading-card" aria-busy="true">
            <SuspenseLoaderToken />
            <p>{t("common.loading")}</p>
          </div>
        ) : (
          <LazyGlobalHubPage
            state={state}
            t={t}
            session={session}
            shellGovernance={roleAppAccess}
            shellTrainer={trainerAppAccess}
            hubAllowedAppKeys={effectiveLauncherAllowedAppKeys}
            hubAppsReady={hubAccessReady}
          />
        )
      ) : null}
      {route === "hub-app-detail" ? (
        <LazyHubMarketplaceAppDetailPage t={t} />
      ) : null}
      {route === "hub-matchmixer-anmeldungen" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyMatchMixerOpenRegistrationsPage t={t} session={session} />
        ) : null
      ) : null}
      {route === "hub-matchmixer-templates" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyMatchMixerTemplatesPage t={t} />
        ) : null
      ) : null}
      {route === "hub-matchmixer" || route === "hub-schleifchen" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazySchleifchenHubPage state={state} t={t} session={session} />
        ) : null
      ) : null}
      {route === "hub-matchmixer-open" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyMatchMixerOpenRegistrationsPage t={t} session={session} />
        ) : null
      ) : null}
      {route === "hub-matchmixer-turniere" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyMatchMixerOwnTournamentsPage t={t} />
        ) : null
      ) : null}
      {route === "hub-matchmixer-statistik" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyMatchMixerStatsPage t={t} />
        ) : null
      ) : null}
      {route === "hub-clubmeisterschaft-statistik" ? (
        !launcherAccessReady ? (
          <SuspenseLoaderToken />
        ) : canAccessCurrentRouteApp ? (
          <LazyClubmeisterschaftStatsPage t={t} />
        ) : null
      ) : null}
      {route === "hub-clubmeisterschaft" ? (
        <LazyClubmeisterschaftHubPage state={state} t={t} session={session} />
      ) : null}
      {route === "hub-tennis-live" ||
      route === "hub-tour" ||
      route === "tenniswelt" ? (
        <LazyTennisweltPage t={t} />
      ) : null}
      {route === "hub-ki-assistant" ? (
        !canUseBallmatesKi ? (
          <Navigate to="/hub" replace />
        ) : (
          <LazyKiAssistantPage
            state={state}
            session={session}
            authUserLabel={authUserLabel}
          />
        )
      ) : null}
    </Suspense>
  ) : isPublicPlayerMatches ? (
    <Suspense fallback={fallback}>
      <LazyPlayerMatchesPage state={state} />
    </Suspense>
  ) : (
    <>
      {(() => {
        if (
          !breadcrumbRoute ||
          !breadcrumbApi ||
          isMatchmixerScope ||
          isClubmeisterschaftScope
        )
          return null;
        try {
          const steps = breadcrumbApi.generateFullBreadcrumb
            ? breadcrumbApi.generateFullBreadcrumb(
                breadcrumbRoute || route,
                state,
                route.startsWith("matchmixer") ||
                  route.startsWith("schleifchen")
                  ? normalizeMatchmixerRoute
                  : null,
              )
            : [];
          const flowSteps = Array.isArray(steps)
            ? steps.filter((s) => s?.level === "flow")
            : [];
          if (!flowSteps.length) return null;
        } catch (_) {
          return null;
        }
        return (
          <section className="card">
            <breadcrumbApi.Breadcrumb
              currentRoute={breadcrumbRoute || route}
              state={state}
              t={t}
              normalizeRoute={
                route.startsWith("matchmixer") ||
                route.startsWith("schleifchen")
                  ? normalizeMatchmixerRoute
                  : null
              }
            />
          </section>
        );
      })()}
      <Suspense fallback={fallback}>
        {route === "login" ? <LoginPage state={state} /> : null}
        {route === "reset-password" ? (
          <ResetPasswordPage state={state} />
        ) : null}
        {route === "auth-callback" ? <AuthCallbackPage state={state} /> : null}
        {route === "public-parent-confirm" ? (
          <LazyPublicParentConfirmPage t={t} />
        ) : null}
        {route === "public-session-rsvp" ? (
          <LazyPublicSessionRsvpPage t={t} />
        ) : null}
        {route === "public-regeltermine-swap" ? (
          <LazyPublicRegeltermineSwapPage t={t} />
        ) : null}
        {route === "public-pay" ? <LazyPublicPayPage t={t} /> : null}
        {route === "public-pay-success" ? (
          <LazyPublicPaySuccessPage t={t} />
        ) : null}
        {route === "public-pay-cancel" ? (
          <LazyPublicPayCancelPage t={t} />
        ) : null}
        {route === "public-booking" ? <LazyPublicBookingPage t={t} /> : null}
        {route === "winterdoppel-join" || route === "winterdoppel-me" ? (
          <LazyWinterDoppelJoinHostPage t={t} />
        ) : null}
        {route === "matchmixer-anmelden" ? (
          <LazyMatchMixerRegisterPage t={t} session={session} />
        ) : null}
        {route === "matchmixer-me" ? (
          <LazyMatchMixerPlayerCarePage t={t} />
        ) : null}
        {route === "winterdoppel-confirm-email" ? (
          <LazyWinterDoppelConfirmEmailHostPage t={t} />
        ) : null}
        {route === "winterdoppel-schedule" ? (
          <LazyWinterDoppelScheduleHostPage t={t} />
        ) : null}
        {isSuperadminRoute && roleAppAccess.checked && launcherIsSuperAdmin ? (
          <LazySuperadminApp />
        ) : null}
        {isClubmeisterschaftScope ? (
          !launcherAccessReady ? (
            <SuspenseLoaderToken />
          ) : canAccessCurrentRouteApp ? (
            <ClubmeisterschaftAppWrapper state={state} t={t} />
          ) : null
        ) : route === "login" ||
          route === "reset-password" ||
          route === "auth-callback" ||
          route === "winterdoppel-join" ||
          route === "winterdoppel-me" ||
          route === "matchmixer-anmelden" ||
          route === "matchmixer-me" ||
          route === "winterdoppel-confirm-email" ||
          route === "winterdoppel-schedule" ||
          route === "profile" ||
          route === "dev-qa" ||
          route === "settings" ||
          route === "access-pending" ||
          route === "accept-invite" ||
          route === "access-requests-admin" ? null : (
          <LazyDynamicAppLoader
            route={route}
            loadingLabel={t("common.loading")}
            state={state}
            t={t}
            accessReady={dynamicAppLoaderAccessReady}
            accessAllowed={canAccessCurrentRouteApp}
          />
        )}
        {route === "player-matches" ? (
          <LazyPlayerMatchesPage state={state} />
        ) : null}
        {route === "settings" ? (
          <LazySettingsHubPage
            t={t}
            state={state}
            subscriptionFeatures={tenantSubscriptionAccess?.features || null}
            bypassSubscriptionFeatures={false}
          />
        ) : null}
        {route === "profile" ? <LazyGlobalAccountPage state={state} /> : null}
        {route === "dev-qa" && import.meta.env.DEV ? (
          <LazyDevQaPage t={t} />
        ) : null}
        {route === "access-pending" ? (
          launchAccessContext.knownSuperadmin ||
          __superadminIdentityPending(launchAccessContext) ? (
            <SuspenseLoaderToken />
          ) : (
            <LazyAccessPendingPage state={state} session={session} />
          )
        ) : null}
        {route === "accept-invite" ? (
          <LazyAcceptInvitePage state={state} session={session} />
        ) : null}
        {route === "access-requests-admin" ? (
          <LazyAccessRequestsAdminPage state={state} session={session} />
        ) : null}
        {route === "admin-people" ? (
          <LazyPeopleAdminPage
            state={state}
            shellGovernance={roleAppAccess}
            trainerAppAccess={trainerAppAccess}
          />
        ) : null}
        {route === "admin-people-detail" ? (
          <LazyPlayerDetailPage
            state={state}
            shellGovernance={roleAppAccess}
            trainerAppAccess={trainerAppAccess}
          />
        ) : null}
        {route === "admin-locations" ? (
          <LazyLocationsAdminPage state={state} />
        ) : null}
        {route === "clubhub" ? (
          <LazyClubHubPage
            state={state}
            session={session}
            shellGovernance={roleAppAccess}
          />
        ) : null}
        {contentSlug ? (
          <LazyContentPage state={state} slug={contentSlug} />
        ) : null}
      </Suspense>
      {breadcrumbApi &&
      !isSuperadminRoute &&
      route !== "login" &&
      route !== "reset-password" &&
      route !== "auth-callback" &&
      route !== "player-matches" &&
      route !== "settings" &&
      route !== "profile" &&
      route !== "dev-qa" &&
      route !== "access-pending" &&
      route !== "accept-invite" &&
      route !== "trainingsdesk-invite" &&
      route !== "matchflow-accept" &&
      route !== "access-requests-admin" &&
      route !== "admin-people" &&
      route !== "admin-people-detail" &&
      route !== "admin-locations" &&
      !contentSlug &&
      !resolveAppKeyFromRoute(String(route || "").trim()) &&
      !resolveLoaderAppKeyFromRoute(String(route || "").trim()) &&
      !breadcrumbApi.findAppIdForRoute?.(route) ? (
        <section className="card">
          <h3 className="mb0">{t("react.routeNotMapped.title")}</h3>
          <p className="muted" style={{ marginTop: 8 }}>
            {t("react.routeNotMapped.hint")} <code>{route}</code>
          </p>
          <div className="row row--wrap">
            <Link to="/" className="btn btn--solid">
              {t("common.goHome")}
            </Link>
          </div>
        </section>
      ) : null}
    </>
  );

  const wrapped = scopeAppKey ? (
    <div data-app={scopeAppKey} className="app-shell__app-scope">
      {content}
    </div>
  ) : (
    content
  );
  const canSeeUserManagement = canAccessAccessRequestsAdmin(
    roleAppAccess.bindings,
    session?.user,
  );
  const isMobileEmbed =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1";
  const hidePlatformChrome =
    route === "login" ||
    route === "reset-password" ||
    route === "auth-callback" ||
    route === "matchflow-accept" ||
    route === "trainingsdesk-invite" ||
    isMobileEmbed;
  const globalAiDisabled =
    !canUseBallmatesKi ||
    hidePlatformChrome ||
    route === "hub-ki-assistant" ||
    shellAccessRestricted;
  return (
    <LoadingOverlayProvider t={t}>
      <NavigationLoaderProvider>
        <GlobalAiAssistantRoot
          t={t}
          authenticated={Boolean(session)}
          userDisplayName={authUserLabel}
          disabled={globalAiDisabled}
        >
          <AppShell
            t={t}
            hidePlatformChrome={hidePlatformChrome}
            footer={!isMobileEmbed}
            authenticated={Boolean(session)}
            authUserLabel={authUserLabel}
            authUserAvatar={authUserAvatar}
            authUserInitials={authUserInitials}
            launcherAccess={{
              accessReady: hubAccessReady,
              isSuperAdmin: launcherIsSuperAdmin,
              allowedAppKeys: effectiveLauncherAllowedAppKeys,
            }}
            onSignOut={onSignOut}
            onSignIn={onSignIn}
            enablePlatformAppSwitcher={true}
            canAccessAccessRequestsAdmin={canSeeUserManagement}
            canAccessVereinsabo={canSeeVereinsabo}
            subscriptionFeatures={tenantSubscriptionAccess?.features || null}
            bypassSubscriptionFeatures={false}
            shellAppTitleKey={shellIdentity?.titleKey || ""}
            shellAppSubtitleKey={shellIdentity?.subtitleKey || ""}
            shellScopeAppKey={scopeAppKey || ""}
            shellAccessRestricted={shellAccessRestricted}
            homeRoute="hub"
            homeMode={route === "hub"}
            onGoHome={goToHubHome}
            coachDeskSelfProfileHref={coachDeskSelfProfileHref}
            userClubs={shellUserClubs}
          >
            {wrapped}
          </AppShell>
          <AppToastHost t={t} />
        </GlobalAiAssistantRoot>
      </NavigationLoaderProvider>
    </LoadingOverlayProvider>
  );
}
